// 《掼蛋》整套界面（DOM，新中式宣纸朱墨；设计基准 1920×1080，手机横竖屏专门布局）。界面流程：
// 首页 → 开局设置 → 牌桌（发牌 → 出牌 → 一副结算 → 还牌 → 下一副）→ 战绩金榜；游戏中菜单（暂停）、记牌器、规则与设置、新手教学。
// 你坐 0 号（下方），搭档老顾 2 号（上方），对手雷哥 1 号（右，下家）、苏小算 3 号（左，上家）。
// 只读模拟层状态、只调 act / nextRound，所有判定在 src/game/。设计说明：产出文件夹 02_界面设计/界面设计说明.md。
import './style.css';
import type { Client } from 'colyseus.js';
import { cardName, isJoker, isWild, power, rankName, sortHand } from './game/cards.ts';
import type { Card } from './game/cards.ts';
import { candidates, choose, DIFFICULTY_NAME } from './game/bot.ts';
import type { Difficulty } from './game/bot.ts';
import { COUNTER_RANKS, unseenCounts } from './game/counter.ts';
import { act, createMatch, defaultReturn, nextRound, partnerOf, target, viewOf, visibleCount } from './game/match.ts';
import type { Match, Seat } from './game/match.ts';
import { characterById, DEFAULT_CAST, NARRATOR, personaOf, ROSTER, setCast } from './game/personas.ts';
import { VoicePlayer } from './voice.ts';
import { URGE_AFTER_MS, URGE_AGAIN_MS, URGE_MAX, VoiceGate } from './game/voiceplan.ts';
import { isRoomCode, type ClientCommand, type Phase, type SeatInfo, type ServerMsg, type TableCommand } from './game/online.ts';
import { analyze, beats, isBombType, legalPlays, PLAY_NAME } from './game/rules.ts';
import type { Play } from './game/rules.ts';
import { rulesFor } from './game/ruleset.ts';
import { arrangeCombos, arrangePlans } from './game/sorter.ts';
import { outcomes, roundHistory } from './game/score.ts';
import { Sound } from './audio.ts';
import { cardFace, ART_IDS } from './cardface.ts';
import { TITLES, NICKNAMES, titleIndex, nextTitle, nicknamesOf, normalizeCareer, type CareerLike } from './game/titles.ts';
// 人物插画先预载：手牌每次选牌都会重画，没缓存时会闪一下空牌心

import { GUIDE } from './guide.ts';
import type { SfxEvent } from './audio.ts';
import { stationOf, STATIONS } from './route.ts';
import { drawRouteMap } from './routeMap.ts';

const BOT_DELAY_MS = 650;
const ME: Seat = 0;
const PLACE = ['上游', '二游', '三游', '下游'];
const POS = ['我', '下家', '对家', '上家'];
let NAMES: string[] = POS.map((p) => p); // 我 + 三位牌友的名字（setCast 后由 refreshNames 更新）
const refreshNames = () => { NAMES = POS.map((p, s) => (s === 0 ? p : personaOf(s)?.name ?? p)); };
const $ = (id: string) => document.getElementById(id)!;
const btn = (id: string) => $(id) as HTMLButtonElement;
const show = (id: string, on = true) => { $(id).hidden = !on; };
// 性能优化（作者 11-03：优化页面和资产加载）：人头牌图首页用不上，等首页加载完、浏览器空闲时再预载，不跟首页插画抢网速
const ric = (window as unknown as { requestIdleCallback?: (f: () => void, o?: object) => void }).requestIdleCallback;
const idle = (fn: () => void) => (ric ? ric(fn, { timeout: 4000 }) : globalThis.setTimeout(fn, 1500));
let artPreloaded = false;
const preloadArt = () => {
  if (artPreloaded) return; artPreloaded = true;
  // 下载并提前解码（decode）：人头牌与当前皮肤的牌桌背景；进牌桌时就不用再解码大图（实测开局那一下的长任务主要在这里）
  const srcs = [...ART_IDS.map((id) => `cards/${id}.webp`), `skins/${settings.skin}.webp`];
  for (const src of srcs) { const im = new Image(); im.decoding = 'async'; im.src = src; void im.decode?.().catch(() => undefined); }
};
window.addEventListener('load', () => idle(preloadArt), { once: true });
// 首页开着时 body 带 at-home：牌桌背景图先不加载（被首页盖住看不见）；离开首页就加载背景、补预载人头牌
const syncAtHome = () => {
  const home = !$('ovHome').hidden;
  document.body.classList.toggle('at-home', home);
  if (home) return;
  for (const img of document.querySelectorAll<HTMLImageElement>('img.art-img[data-src]')) { img.src = img.dataset.src!; img.removeAttribute('data-src'); }
  preloadArt();
};
new MutationObserver(syncAtHome).observe($('ovHome'), { attributes: true, attributeFilter: ['hidden'] }); // 初次同步放在启动流程最后（决定先进首页还是直接进牌桌之后）

// ── 设置与本地存档 ──
const params = new URLSearchParams(location.search);
const seedParam = Number(params.get('seed'));
const testMode = Number.isFinite(seedParam) && seedParam > 0; // ?seed= 直接进牌桌，不读写存档（测试与缺陷复现）
const store = <T,>(k: string, d: T): T => { try { const v = localStorage.getItem(`gd.${k}`); return v === null ? d : (JSON.parse(v) as T); } catch { return d; } };
const put = (k: string, v: unknown) => { try { localStorage.setItem(`gd.${k}`, JSON.stringify(v)); } catch { /* 隐私模式不能存 */ } };
/** 三套武侠主题皮肤：擂台论剑（默认，作者 11-03 定）、水墨江湖、客栈牌局；背景图 public/skins/<id>.webp，其余全是 CSS（body[data-skin]）。 */
type Skin = 'ink' | 'inn' | 'arena';
const settings = {
  fx: params.get('fx') === '0' ? false : store('fx', !matchMedia('(prefers-reduced-motion: reduce)').matches),
  talk: params.get('talk') === '0' ? false : store('talk', true),
  sound: params.get('sound') === '0' ? false : store('sound', true),
  tut: params.get('tut') === '0' ? false : store('tut', true),
  level: store<Difficulty>('level', 'normal'),
  limit: store('limit', 0),
  voice: params.get('voice') === '0' ? false : store('voice', true),
  me: store('me', 'xiaozhou'), // 我选的角色（作者：开局可以选择角色）
  skin: (['ink', 'inn', 'arena'].includes(params.get('skin') ?? '') ? params.get('skin')! : store('skin', 'arena')) as Skin, // 主题皮肤（作者 11-03：三套武侠皮肤都要，可切换；默认擂台论剑）
  cast: store<Record<1 | 2 | 3, string>>('cast', { ...DEFAULT_CAST }),
};
if (testMode) { settings.me = 'xiaozhou'; settings.cast = { ...DEFAULT_CAST }; } // 测试与复现固定阵容
setCast(settings.cast);
refreshNames();
type OnlineRoom = Awaited<ReturnType<Client['create']>>;
interface OnlineState {
  room: OnlineRoom | null;
  code: string;
  seats: (SeatInfo | null)[];
  phase: Phase;
  inGame: boolean;
  host: boolean;
  stateVersion: number;
  pending: Map<string, ClientCommand>;
  reconnectAttempt: number;
  reconnectTimer: number | null;
}
let online: OnlineState | null = null;
const voice = new VoicePlayer();
/** 座位上是谁：0 号是我选的角色，其他按阵容。 */
const charOf = (seat: Seat) => (seat === ME ? characterById(online?.seats[0]?.char ?? settings.me) : personaOf(seat));
const sound = new Sound();
sound.muted = !settings.sound;
const sfx = (ev: SfxEvent) => sound.play(ev);
window.addEventListener('pointerdown', () => sound.unlock(), { capture: true }); // iOS 要在手势里解锁声音
const applyVoice = () => { voice.enabled = settings.voice && settings.sound; };
applyVoice();

interface MatchStats { start: number; bombs: number; firsts: number; level: Difficulty; wild?: number; behind?: number } // wild：用逢人配凑成炸弹 / 同花顺；behind：本局最多落后几级（名号与绰号用）
type Career = CareerLike;
/** 本机生涯战绩：旧存档缺“问鼎、最远到过、绰号计数”时按 0 补（normalizeCareer），键名不变。 */
const career = (): Career => normalizeCareer(store<Partial<Career>>('career', {}));
/** 个人档案（作者 11-03）：昵称默认用所选角色的名字，改过就用自己的（≤ 8 字）；好友联机也用这个名字。 */
const myName = (): string => store('nick', '') || (characterById(settings.me)?.name ?? '我');
const myTitle = (): string => TITLES[titleIndex(career())].name;

let nextSeed = testMode ? seedParam : Date.now() % 1e9;
let m: Match = createMatch(nextSeed, rulesFor(settings.limit));
let stats: MatchStats = { start: Date.now(), bombs: 0, firsts: 0, level: settings.level };
const save = () => { if (!testMode && !online) put('save', m.winner === null ? { m, stats } : null); }; // 联机局不存本机（服务端是准）

const lastShown = new Map<Seat, Play | null>(); // 本圈每家最后一次动作
const selected = new Set<number>();
let sortMode: 'rank' | 'combo' = 'rank';
let timer = 0;
let dealing = false;
let finalMode: 'match' | 'career' = 'match';
const portrait = matchMedia('(orientation: portrait) and (max-width: 500px)');
const compact = matchMedia('(max-width: 500px), (max-height: 500px)');
/** 首页或任何弹层开着时暂停机器人。 */
/** 手机竖着拿：显示“横过来玩”并暂停（作者 11-02：手机锁横版）。和 style.css 的 .rotate-hint 媒体查询一致。 */
const mustRotate = matchMedia('(orientation: portrait) and (max-width: 500px)');
const paused = () => mustRotate.matches || ['ovHome', 'ovOnline', 'ovSetup', 'ovMenu', 'ovRules', 'ovCounter', 'ovScore', 'ovReturn', 'ovResult', 'ovFinal', 'ovUserCenter'].some((id) => !$(id).hidden);

/** 同一方拿上游、二游时，另外两家都是“双下”，不分三游下游（竞赛规则第二条 10）。 */
const placeLines = (order: Seat[]) =>
  order[0] % 2 === order[1] % 2
    ? [`上游 ${NAMES[order[0]]}`, `二游 ${NAMES[order[1]]}`, `双下 ${NAMES[order[2]]}、${NAMES[order[3]]}`]
    : order.map((s, i) => `${PLACE[i]} ${NAMES[s]}`);

// ── 牌面 ──
function cardEl(c: Card, level: number, tag: 'div' | 'button' = 'div'): HTMLElement {
  const el = document.createElement(tag);
  el.dataset.id = String(c.id);
  const red = c.suit === 'H' || c.suit === 'D' || c.rank === 17;
  el.className = `card${isJoker(c) ? ' joker' : ''}${red ? ' red' : ''}${!isJoker(c) && c.rank === level ? (isWild(c, level) ? ' wild lvl' : ' lvl') : ''}`;
  el.innerHTML = cardFace(c); // 扑克牌面：角标、点阵、宫廷牌、大小王（src/cardface.ts）
  el.setAttribute('aria-label', cardName(c));
  return el;
}

// ── 理牌：按点数；或一键理牌（炸弹、同花顺、连牌、三张、对子、单张成组，src/game/sorter.ts） ──
function arrange(hand: Card[], level: number): Card[][] {
  if (sortMode === 'combo') return arrangeCombos(hand, level).map((g) => g.cards);
  const sorted = sortHand(hand, level);
  return [sorted.filter(isJoker), sorted.filter((c) => isWild(c, level)), sorted.filter((c) => !isJoker(c) && !isWild(c, level))].filter((g) => g.length);
}

/**
 * 手牌竖排（作者 11-02 参考腾讯掼蛋横版：同点数叠成一列、各列并排，牌面大、好点）：
 * 按点数时一列一个点数（大小王各一列，级牌与逢人配一列）；一键理牌时一组一列，炸弹 / 同花顺 / 顺子等在列底标牌型，
 * 对子、三张按点数分列，单张各一列。从左到右由大到小。
 */
const CN = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
/** 一组牌：一列或相邻几列（手机上长组拆列、三带对是三张一列 + 对子一列），牌型签贴在整组正上方（作者 11-02：不遮挡牌面图案） */
interface HandGrp { cols: Card[][]; tag: string; as?: Record<number, string>; switchTo?: string } // as：逢人配当什么牌；switchTo：点签切换到的下一个方案（作者 11-03 理牌规格）
/** 牌型签用竞赛规则里的名字（作者 11-02）；炸弹按张数叫“四炸 / 五炸…”（作者原话）；对子、单张不贴 */
const TAG: Record<string, string> = { 四大天王: '四大天王', 同花顺: '同花顺', 逢人配: '逢人配', 钢板: '三同连张', 三连对: '三连对', 顺子: '顺子', 三带对: '三带对', 三张: '三同张' };
/** 手机上一列最多 4 张（v0.8 实测：5–6 张的列会把牌带压到 17px），更长的列拆成相邻几列，仍在同一组里。 */
function splitTall(grps: HandGrp[]): HandGrp[] {
  const max = compact.matches ? 4 : 6;
  return grps.map((g) => ({ ...g, cols: g.cols.flatMap((c) => {
    const out: Card[][] = [];
    for (let i = 0; i < c.length; i += max) out.push(c.slice(i, i + max));
    return out;
  }) }));
}
function handColumns(hand: Card[], level: number): HandGrp[] {
  return splitTall(handColumns0(hand, level));
}
function handColumns0(hand: Card[], level: number): HandGrp[] {
  const byKey = (cs: Card[]) => {
    const m = new Map<number, Card[]>();
    for (const c of sortHand(cs, level)) { const k = isJoker(c) ? c.rank * 100 : power(c.rank, level); m.set(k, [...(m.get(k) ?? []), c]); }
    return [...m.values()];
  };
  if (sortMode !== 'combo') return byKey(hand).map((cards) => ({ cols: [cards], tag: '' }));
  const out: HandGrp[] = [];
  // 一键理牌 v2：默认最优方案；有候选方案时，在同花顺那组（没有就是第一处不同的那组）的签上点 ⇄ 切到下一个方案，其余牌组联动重排
  const plans = arrangePlans(hand, level);
  const hk = `${level}|${hand.map((c) => c.id).join(',')}`;
  if (hk !== planHand) { planHand = hk; planIdx = 0; }
  const plan = plans[planIdx % plans.length], next = plans.length > 1 ? plans[(planIdx + 1) % plans.length] : null;
  const sigOf = (g: { label: string; cards: Card[] }) => `${g.label}:${g.cards.map((c) => c.id).sort((a, b) => a - b).join('.')}`;
  const nextSigs = new Set(next?.groups.map(sigOf));
  const sw = next ? (plan.groups.find((g) => g.label === '同花顺') ?? plan.groups.find((g) => g.label !== '对子' && g.label !== '单张' && !nextSigs.has(sigOf(g)))) : undefined;
  for (const g of plan.groups) {
    const extra = { ...(g.as ? { as: g.as } : {}), ...(g === sw && next ? { switchTo: `方案 ${(planIdx % plans.length) + 1}/${plans.length}：${plan.note}；点一下换成 ${next.note}` } : {}) };
    if (g.label === '对子') out.push(...byKey(g.cards).map((cards) => ({ cols: [cards], tag: '' })));
    else if (g.label === '单张') out.push(...sortHand(g.cards, level).map((c) => ({ cols: [[c]], tag: '' })));
    else if (g.label === '三带对') out.push({ cols: [g.cards.slice(0, 3), g.cards.slice(3)], tag: TAG[g.label], ...extra });
    else out.push({ cols: [g.cards], tag: g.label === '炸弹' ? `${CN[g.cards.length] ?? g.cards.length}炸` : TAG[g.label] ?? g.label, ...extra });
  }
  return out;
}

let handKey = '';
let planIdx = 0, planHand = ''; // 一键理牌当前用第几个候选方案（换了手牌就回到最优方案）
function renderHand(): void {
  const r = m.round;
  // 手牌没变（牌、选中、理牌方式、屏幕尺寸）就不重画：机器人每 0.65 秒出一手，重画会把正要点的牌换掉
  const key = [sortMode, planIdx, portrait.matches, innerWidth, innerHeight, r.level, r.hands[ME].map((c) => c.id).join(','), [...selected].sort().join(',')].join('|');
  if (key === handKey) return;
  handKey = key;
  const hand0 = $('hand');
  if (!portrait.matches) {
    // 电脑与手机横屏：竖列
    hand0.classList.add('cols');
    hand0.classList.toggle('deal', dealing);
    let k = 0;
    hand0.replaceChildren(...handColumns(r.hands[ME], r.level).map((g) => {
      const grp = document.createElement('div');
      grp.className = 'grp';
      grp.append(...g.cols.map((cards) => {
        const div = document.createElement('div');
        div.className = 'col';
        div.append(...cards.map((c) => {
          const el = cardEl(c, r.level, 'button');
          el.style.setProperty('--i', String(k++));
          if (selected.has(c.id)) el.classList.add('sel');
          if (g.as?.[c.id]) { el.dataset.as = g.as[c.id] === '本色' ? '配·本色' : `配→${g.as[c.id]}`; el.title = g.as[c.id] === '本色' ? '逢人配：这里按红桃级牌本身用' : `逢人配：在这组里当 ${g.as[c.id]} 用`; } // 逢人配去向（作者 11-03：“当J”看不懂，保留“配”字再写去向）
          return el;
        }));
        return div;
      }));
      if (g.tag) {
        grp.dataset.tall = String(Math.max(...g.cols.map((c) => c.length)));
        const tag = Object.assign(document.createElement(g.switchTo ? 'button' : 'span'), { className: /炸$|同花顺|四大天王/.test(g.tag) ? 'col-tag bomb' : 'col-tag', textContent: g.switchTo ? `${g.tag} ⇄` : g.tag });
        if (g.switchTo) { tag.classList.add('switch'); tag.title = g.switchTo; tag.setAttribute('aria-label', g.switchTo); tag.onclick = (e) => { e.stopPropagation(); planIdx++; handKey = ''; hand0.classList.add('regroup'); render(); sfx('button'); }; }
        grp.append(tag);
      }
      return grp;
    }));
    dealing = false;
    if (hand0.classList.contains('regroup')) window.setTimeout(() => hand0.classList.remove('regroup'), 300);
    fitHand();
    return;
  }
  hand0.classList.remove('cols');
  const groups = arrange(r.hands[ME], r.level);
  const flat: (Card | null)[] = groups.flatMap((g, i) => (i ? [null, ...g] : g)); // null = 组间缝
  const rows: (Card | null)[][] = [];
  if (portrait.matches && r.hands[ME].length > 14) {
    // 竖屏两行：按张数均分，附近有组缝就在组缝处断开
    const half = Math.ceil(r.hands[ME].length / 2);
    let count = 0, cut = flat.length;
    for (let i = 0; i < flat.length; i++) { if (flat[i]) count++; if (count >= half) { cut = i + 1; break; } }
    const gapNear = flat.findIndex((x, i) => x === null && Math.abs(i - cut) <= 2);
    if (gapNear > 0) cut = gapNear;
    const trim = (row: (Card | null)[]) => row.filter((x, i, a) => !(x === null && (i === 0 || i === a.length - 1)));
    rows.push(trim(flat.slice(0, cut)), trim(flat.slice(cut)));
  } else rows.push(flat);
  const hand = $('hand');
  hand.classList.toggle('deal', dealing);
  let i = 0;
  hand.replaceChildren(...rows.map((row) => {
    const div = document.createElement('div');
    div.className = 'row';
    div.append(...row.map((c) => {
      if (!c) return Object.assign(document.createElement('span'), { className: 'gap' });
      const el = cardEl(c, r.level, 'button');
      el.style.setProperty('--i', String(i++));
      if (selected.has(c.id)) el.classList.add('sel');
      return el;
    }));
    return div;
  }));
  dealing = false;
  fitHand();
}

/** 每行的叠放宽度按可用宽度现算：放得下就用设计值，放不下就收紧（作者要求：卡牌不得超出界面）。 */
function fitHand(): void {
  const hand = $('hand');
  if (hand.classList.contains('cols')) { fitColumns(hand); return; }
  const cs = getComputedStyle(hand);
  const avail = hand.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  const probe = hand.querySelector('.card') as HTMLElement | null;
  if (!probe) return;
  const cw = probe.getBoundingClientRect().width;
  const design = parseFloat(getComputedStyle(probe).getPropertyValue('--strip')) || cw * 0.52;
  const gapw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gapw')) || 0;
  for (const row of hand.querySelectorAll<HTMLElement>('.row')) {
    const n = row.querySelectorAll('.card').length;
    // 每组最后一张后面是缝，不被压住，占满整张牌宽：总宽 = 组数×牌宽 + (张数−组数)×露出宽 + 缝数×缝宽
    const fit = () => {
      const gaps = row.querySelectorAll('.gap').length;
      const groups = gaps + 1;
      const gw = gaps ? (row.querySelector('.gap') as HTMLElement).getBoundingClientRect().width || gapw : 0;
      return n > groups ? (avail - groups * cw - gaps * gw) / (n - groups) : design;
    };
    let strip = fit();
    // 太挤就去掉组间缝，整排连续叠放
    if (strip < design * 0.75 && row.querySelector('.gap')) { row.querySelectorAll('.gap').forEach((g) => g.remove()); strip = fit(); }
    if (n < 2) row.style.removeProperty('--strip');
    else row.style.setProperty('--strip', `${Math.min(design, strip)}px`);
  }
}
/**
 * 竖列尺寸：列宽按可用宽度均分（不超过设计牌宽），列高不超过手牌区上限；每张露出的“牌带”尽量取牌高一半，
 * 最少取牌高 0.3（不够就整体缩小牌）。所有数值写在 #hand 的 --cw / --ch / --band 上，牌面字号跟着 --cw 走。
 */
function fitColumns(hand: HTMLElement): void {
  const cols = [...hand.querySelectorAll<HTMLElement>('.col')];
  if (!cols.length) return;
  const cs = getComputedStyle(hand);
  const W = hand.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  const phone = compact.matches;
  const H = innerHeight * (phone ? 0.44 : 0.36);
  const gap = phone ? 3 : 6;
  const C = cols.length, L = Math.max(...cols.map((c) => c.querySelectorAll('.card').length));
  const px = Math.min(innerWidth / 1920, innerHeight / 1080);
  const design = phone ? 64 : Math.max(56, 100 * px);
  const byW = (W - (C - 1) * gap) / C;
  const byH = H / (1.4 * (1 + (L - 1) * 0.3));
  const cw = Math.floor(Math.min(design, byW, byH));
  const ch = Math.round(cw * 1.4);
  const band = L > 1 ? Math.floor(Math.min(ch * 0.5, (H - ch) / (L - 1))) : 0;
  hand.style.setProperty('--cw', `${cw}px`);
  hand.style.setProperty('--ch', `${ch}px`);
  hand.style.setProperty('--band', `${band}px`);
  hand.style.setProperty('--colgap', `${gap}px`);
  // 牌型签排成一行，放在最高那列的上方（手机列比签窄，贴在组头上会压到隔壁的牌）；手牌区往上让出这一行
  const tags = [...hand.querySelectorAll<HTMLElement>('.col-tag')];
  hand.style.setProperty('--tagroom', tags.length ? `${phone ? 28 : Math.max(32, Math.round(38 * px))}px` : '0px');
  if (tags.length) placeTags(hand, tags, ch + (L - 1) * band);
}
/** 每个签对准自己那组的正中；相邻的签挨上了就往右让 4px，整行出界再整体左移。 */
function placeTags(hand: HTMLElement, tags: HTMLElement[], colH: number): void {
  const hr = hand.getBoundingClientRect();
  const items = tags.map((t) => { const g = t.parentElement!.getBoundingClientRect(); t.style.bottom = `${colH + (compact.matches ? 6 : 8)}px`; return { t, g, w: t.offsetWidth, x: g.left + g.width / 2 - t.offsetWidth / 2 }; });
  let right = -Infinity;
  for (const it of items) { it.x = Math.max(it.x, right + 4); right = it.x + it.w; }
  const over = right - (hr.right - 4);
  for (const it of items) { if (over > 0) it.x -= over; it.x = Math.max(it.x, hr.left + 4); it.t.style.left = `${it.x - it.g.left}px`; }
}
window.addEventListener('resize', () => { handKey = ''; render(); });

// 选牌：点一下切换；按住滑过连选（手机友好）。pointer 事件统一鼠标与触屏
let dragMode: boolean | null = null;
let dragLast = -1;
function toggleAt(el: Element | null, force?: boolean): boolean | null {
  const ce = (el as HTMLElement | null)?.closest?.('#hand .card') as HTMLElement | null;
  if (!ce) return null;
  const id = Number(ce.dataset.id);
  if (id === dragLast && force !== undefined) return force;
  dragLast = id;
  const on = force ?? !selected.has(id);
  if (on) selected.add(id); else selected.delete(id);
  ce.classList.toggle('sel', on);
  sfx(on ? 'select' : 'deselect');
  handKey = ''; // 选中状态变了，下次 render 以当前选中重算
  checkSelection();
  return on;
}

/**
 * 出牌前实时校验（玩家反馈 2026-11-02）：选牌时就判断牌型与牌力，提示行写明“可以出：××”或不能出的原因，
 * 不能出时出牌按钮置灰；点了也只提示、不出牌。判定与引擎同一套（legalPlays / analyze），引擎 act 仍会再拦一次。
 */
function checkSelection(): boolean {
  const r = m.round;
  const b = btn('btnPlay');
  b.classList.remove('invalid');
  const myTurn = r.turn === ME && !r.over && !r.finished.includes(ME);
  const cards = r.hands[ME].filter((c) => selected.has(c.id));
  if (!myTurn || !cards.length) return false;
  const t = target(r);
  const ok = legalPlays(cards, r.level, t);
  if (ok.length) { $('msg').textContent = `可以出：${[...new Set(ok.map((p) => PLAY_NAME[p.type]))].join(' 或 ')}`; return true; }
  b.classList.add('invalid');
  const as = analyze(cards, r.level);
  $('msg').textContent = !as.length ? `不成牌型（选了 ${cards.length} 张）`
    : `压不住：桌上是${PLAY_NAME[t!.type]}，你选的是${PLAY_NAME[as[0].type]}${as[0].type === t!.type && as[0].size === t!.size ? '，不够大' : '，牌型或张数不同'}`;
  return false;
}
// 提示行按内容分三种语气（作者 11-02：按钮下的提示文字要跟三个按钮一套设计）：可以出 → 黛绿签，压不住 / 不成牌型 → 朱红签，其余 → 墨色签
new MutationObserver(() => {
  const t = $('msg').textContent ?? '';
  $('msg').dataset.tone = !t ? '' : t.startsWith('可以出') ? 'ok' : /^(压不住|不成牌型)/.test(t) ? 'bad' : 'info';
  requestAnimationFrame(() => placeCoach()); // 提示行冒出来 / 变长时新手提示条立刻让开，不等 0.4 秒的定时器（发布前回归里抓到的偶发遮挡）
}).observe($('msg'), { childList: true, characterData: true, subtree: true });
$('hand').addEventListener('pointerdown', (e) => { dragLast = -1; dragMode = toggleAt(e.target as Element); clearChoices(); });
window.addEventListener('pointermove', (e) => { if (dragMode !== null && e.buttons) toggleAt(document.elementFromPoint(e.clientX, e.clientY), dragMode); });
const endDrag = () => { dragMode = null; };
window.addEventListener('pointerup', endDrag);
window.addEventListener('pointercancel', endDrag);

// ── ① “掼”的手感：都放在 .table 里（越界裁掉），不盖按钮与手牌 ──
const table = $('table');
const centerOf = (el: Element) => {
  const t = table.getBoundingClientRect(), b = el.getBoundingClientRect();
  return { x: b.left - t.left + b.width / 2, y: b.top - t.top + b.height / 2 };
};
function spawn(cls: string, text: string, at?: { x: number; y: number }, ms = 1300): HTMLElement {
  const el = Object.assign(document.createElement('div'), { className: cls, textContent: text });
  if (at) { el.style.left = `${at.x}px`; el.style.top = `${at.y}px`; }
  table.append(el);
  setTimeout(() => el.remove(), ms);
  return el;
}
function playFx(seat: Seat, p: Play): void {
  sfx(p.type === 'jokerBomb' ? 'jokerBomb' : p.type === 'straightFlush' ? 'straightFlush' : isBombType(p.type) ? 'bomb' : 'play');
  if (!settings.fx) return;
  const mini = $(`play${seat}`).querySelector('.mini');
  mini?.classList.add('slam');
  if (!isBombType(p.type)) return;
  mini?.classList.add('heavy');
  const at = centerOf($(`play${seat}`));
  spawn('ink', '', at, 900);
  table.classList.remove('shake'); void table.offsetWidth; table.classList.add('shake');
  if (p.type === 'jokerBomb') spawn('calli', '天王');
  else spawn('stamp', p.type === 'straightFlush' ? '顺' : '炸', { x: at.x, y: at.y - 10 }, 1100);
}
// ── 规则播报的专属特效（作者 11-03：每个跟规则相关的播报都要有对应特效，且互不重复）──
// 上游 = 金色“上游”方印；炸弹 = 墨晕 + 朱红“炸 / 顺”方印；四大天王 = 大字“天王”；借风 = 风线卷“借风”飞向搭档；
// 报牌 = 朱红令旗升起；还剩两张 = 警钟波纹圆章；不要 = 淡墨烟“过”；进贡 = 金色“贡”方印弧线飞；还牌 = 青玉“还”圆印反向飞；抗贡 = 金边红盾
const seatAt = (s: Seat) => (s === ME ? centerOf($('play0')) : centerOf($(`s${s}`)));
/** 从 a 飞到 b（中途抬高，走一条弧线）。 */
function flyFx(cls: string, text: string, from: Seat, to: Seat, ms = 1300): void {
  const a = seatAt(from), b = seatAt(to);
  const el = spawn(cls, text, a, ms);
  el.animate([
    { left: `${a.x}px`, top: `${a.y}px`, transform: 'translate(-50%, -50%) scale(.6)', opacity: 0 },
    { offset: 0.2, opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
    { offset: 0.55, left: `${(a.x + b.x) / 2}px`, top: `${Math.min(a.y, b.y) - 60}px` },
    { offset: 0.85, left: `${b.x}px`, top: `${b.y}px`, opacity: 1, transform: 'translate(-50%, -50%) scale(1.05)' },
    { left: `${b.x}px`, top: `${b.y}px`, opacity: 0, transform: 'translate(-50%, -50%) scale(.9)' },
  ], { duration: ms, easing: 'ease-in-out', fill: 'forwards' });
}
function windFx(from: Seat, to: Seat): void {
  sfx('borrowWind');
  if (!settings.fx) return;
  flyFx('fx-wind', '借风', from, to, 1500); // 原来只是一行 26px 小字，作者 11-03 没看到——改成风线卷大字
}
/** 报牌：朱红令旗从座位升起，写“报牌 N”。 */
function reportFx(seat: Seat, n: number): void {
  if (!settings.fx) return;
  const el = spawn('fx-report', '', seatAt(seat), 1600);
  el.innerHTML = `<b>报牌</b><i>${n}</i>`;
}
/** 还剩两张：警钟——红色圆章“剩2”向外荡波纹。 */
function lastFx(seat: Seat, n: number): void {
  if (!settings.fx) return;
  spawn('fx-last', `剩${n}`, seatAt(seat), 1700);
}
/** 不要：一缕淡墨烟写“过”，往上飘散。 */
function passFx(seat: Seat): void {
  if (!settings.fx) return;
  spawn('fx-pass', '过', seatAt(seat), 900);
}
/** 抗贡：金边红盾在抗贡的人面前弹出。 */
function resistFx(seat: Seat): void {
  if (!settings.fx) return;
  spawn('fx-resist', '抗贡', seatAt(seat), 1800);
}
function firstFx(seat: Seat): void {
  sfx('first');
  if (!settings.fx) return;
  spawn('stamp gold', '上游', seat === ME ? centerOf($('play0')) : centerOf($(`s${seat}`)), 1300);
}

// ── ② 声音分工（作者 11-02 对齐）：播报员报牌与牌局事实（只出声、不出字）；角色只在等待时说口头禅（催促、闲聊，带气泡） ──
const talking = new Map<number, number>();
const gate = new VoiceGate(); // 语音体系：什么场景说什么、谁先谁后、冷却，见 game/voiceplan.ts 与 docs/语音体系.md
/** 角色说一句口头禅（urge / idle）。bubble 为气泡出现的概率（我自己不出气泡，只有声音）。 */
function speak(seat: Seat, ev: string, bubble = 1): boolean {
  const c = charOf(seat);
  if (!c) return false;
  const pool = (c.lines as Record<string, string[] | undefined>)[ev];
  if (!pool?.length) return false;
  if (!gate.allow(c.id, ev, performance.now(), Math.random())) return false;
  const i = Math.floor(Math.random() * pool.length);
  void voice.sayFile(c.id, `${ev}_${i + 1}.mp3`);
  if (seat === ME || !settings.talk || Math.random() > bubble || talking.has(seat)) return true;
  const el = Object.assign(document.createElement('div'), { className: 'bubble', textContent: pool[i] });
  const t = table.getBoundingClientRect(), s = $(`s${seat}`).getBoundingClientRect();
  if (seat === 2) { el.style.left = `${s.right - t.left + 8}px`; el.style.top = `${s.top - t.top + 4}px`; }
  else if (seat === 3) { el.style.left = `${s.left - t.left}px`; el.style.top = `${s.bottom - t.top + 6}px`; }
  else { el.style.right = `${t.right - s.right}px`; el.style.top = `${s.bottom - t.top + 6}px`; }
  table.append(el);
  talking.set(seat, window.setTimeout(() => { el.remove(); talking.delete(seat); }, 1900));
  return true;
}
const later = (ms: number, fn: () => void) => window.setTimeout(fn, ms);

/** 开场旁白（作者：新局开启时不要角色语音，要一条有记忆点、有趣的开场语音）：桌面中央一行字 + 播报声。 */
let narrUntil = 0; // 开场白播完前机器人先不出牌，免得报牌排队压在开场白后面
function narrate(): void {
  const pool = NARRATOR.lines.opening;
  if (!gate.allow('narrator', 'opening', performance.now(), 0)) return;
  const i = Math.floor(Math.random() * pool.length);
  const voiced = !STALE_VOICE.has(`opening_${i + 1}`);
  if (voiced) void voice.queue(NARRATOR.id, `opening_${i + 1}.mp3`);
  gate.muteChars = true; // 开场白这几秒角色不说话
  later(3600, () => { gate.muteChars = false; });
  if (!testMode) narrUntil = performance.now() + 3600;
  if (!settings.talk) return;
  const el = Object.assign(document.createElement('div'), { className: 'narration' });
  el.innerHTML = `<i class="nseal">开局</i><span>${pool[i]}</span>`;
  table.append(el);
  placeNarration(el);
  later(3600, () => el.remove());
}

/**
 * 开场卷轴放在对家头像下方的空当正中（作者 11-03：别和顶部头像挤在一起）：上下各留 ≥ 12px；
 * 手机上轮到你先出时按钮行就在头像下面，空当放不下，改放到手牌区上方（卷轴不挡点击，3.6 秒后消失）。
 */
function placeNarration(el: HTMLElement): void {
  const tr = table.getBoundingClientRect();
  const vis = (e: Element) => { for (let x: Element | null = e; x; x = x.parentElement) { const c = getComputedStyle(x); if (c.visibility === 'hidden' || c.display === 'none') return false; } return true; };
  const rect = (e: Element) => e.getBoundingClientRect();
  const seatB = Math.max(tr.top, ...[...document.querySelectorAll('#s2, #s2 *')].filter(vis).map((e) => rect(e).bottom));
  const below = [...document.querySelectorAll('.bar .btn, .msgline, #hand .card, .played .card')].filter(vis).map(rect).filter((r) => r.height && r.top > seatB);
  const nextTop = Math.min(tr.bottom, ...below.map((r) => r.top));
  const h = el.offsetHeight, M = 12;
  let cy: number;
  if (nextTop - seatB >= h + 2 * M) cy = (seatB + nextTop) / 2;
  else { const hand = rect($('hand')); cy = hand.top + Math.min(hand.height / 2, h / 2 + 40); }
  el.style.top = `${cy - tr.top}px`;
  el.dataset.place = nextTop - seatB >= h + 2 * M ? 'gap' : 'hand';
}

/** 文字已改、录音还没重新合成的播报：先只显示文字不出声，合成后从这里删掉（11-03 武侠改版的 2 句已重新合成，现在为空）。 */
const STALE_VOICE = new Set<string>();

/** 催促（作者：玩家等待时角色说口头禅）：轮到你 10 秒没出，牌友催一句，优先搭档；一手最多催两次。 */
let urgeTimer = 0;
function armUrge(n = 0): void {
  clearTimeout(urgeTimer);
  if (n >= URGE_MAX) return;
  urgeTimer = window.setTimeout(() => {
    const r = m.round;
    if (r.over || r.turn !== ME) return;
    if (paused()) { armUrge(n); return; }
    const foes = ([1, 3] as Seat[]).filter((x) => !r.finished.includes(x));
    speak(Math.random() < 0.6 || !foes.length ? 2 : foes[Math.floor(Math.random() * foes.length)], 'urge', 1);
    armUrge(n + 1);
  }, n === 0 ? URGE_AFTER_MS : URGE_AGAIN_MS);
}

/** 播报员说一句（报牌或牌局事实）：按顺序排队，不互相打断；第 i 句对应 <事件>_<i>.mp3。 */
function call(ev: string): void {
  const pool = NARRATOR.lines[ev];
  if (!pool?.length || !gate.allow(NARRATOR.id, ev, performance.now(), 0)) return;
  const file = `${ev}_${Math.floor(Math.random() * pool.length) + 1}`;
  if (!STALE_VOICE.has(file)) void voice.queue(NARRATOR.id, `${file}.mp3`);
}
/** 出牌播报（作者：出什么牌都要语音，用一个通用声音）：单张报点数，对子报“对几”，其他报牌型，炸弹报“炸弹”。 */
function announce(p: Play): void {
  const level = m.round.level;
  const rankOf = (cs: Card[]) => { const n = cs.find((c) => !isWild(c, level)); return n ? n.rank : level; };
  if (p.type === 'single') call(`play_single_${rankOf(p.cards)}`);
  else if (p.type === 'pair') call(`play_pair_${rankOf(p.cards)}`);
  else call(`play_${p.type}`);
}

// ── 新手教学：边打边教，每条只出一次（设置里可关；“新手教学局”从头再教） ──
/** 每条提示：一句短话 + 指向哪里（按顺序找第一个看得见的目标）。 */
const TIPS: Record<string, { text: string; at: string[] }> = {
  play: { text: '选好牌点「出牌」；压不住点「不出」，没主意点「提示」。', at: ['#btnPlay'] },
  sort: { text: '牌多眼花？点这里，自动理成炸弹、顺子、对子。', at: ['#btnSortCombo'] },
  wild: { text: '红框带“配”字的是逢人配，能当大小王以外的任意牌。', at: ['#hand .card.wild', '#hand'] },
  bomb: { text: '框里这几张同点牌是炸弹，能压普通牌型，留到对手快走完再用。', at: ['@bomb', '#hand'] }, // @bomb：直接框住手里那组炸弹（试玩：指向理牌按钮会被当成“炸弹按钮”）
  report: { text: '对手剩 10 张以内会报牌，张数亮在这里，记得拦住他。', at: ['#who1 .badge', '#who3 .badge', '#s1'] },
  tribute: { text: '下游进贡最大的牌，上游还一张 10 以下的；有两个大王可抗贡。', at: ['#note', '#msg'] },
  level: { text: '只有上游一方升级：搭档二游升 3 级、三游升 2 级、下游升 1 级。点这里算分。', at: ['#btnScore'] },
};
let tipsDone = new Set<string>(store<string[]>('tips', []));
const tipQueue: string[] = [];
/**
 * 教学提示在牌桌层：结算、还牌等面板开着时会被压在下面看不见、点不着（实打复现 2026-11-02）。
 * 所以面板开着或已有提示时先排队，回到牌桌（step）时再按顺序显示。
 */
/** 轮到我的第几手；一手最多弹一条提示（玩家之眼：第一手前 0.6 秒连弹三条，新手被刷屏），其余留到后面几手。 */
// “这一手”按牌局进度认：第几副 + 桌上已出过几手（step 在同一手里会被调用多次，不能用计数器）
const turnId = () => m.roundNo * 10_000 + m.round.history.length;
let tipTurnShown = -1;
function tip(id: string): void {
  if (!settings.tut || tipsDone.has(id) || tipQueue.includes(id) || $('coach').dataset.tip === id && !$('coach').hidden) return;
  const mine = m.round.turn === ME && !m.round.over;
  if (paused() || !$('coach').hidden || (mine && tipTurnShown === turnId())) { tipQueue.push(id); return; }
  if (mine) tipTurnShown = turnId();
  $('coachText').textContent = TIPS[id].text;
  $('coach').dataset.tip = id;
  show('coach');
  placeCoach(); // 作者 11-03：不再给目标套金色光环（影响美观），靠提示条的尖角指向
  // 作者 11-03：没点“知道了”，显示 5 秒后自动收起，算已看过（不再弹同一条）
  window.clearTimeout(coachTimer);
  if (COACH_AUTO_MS) coachTimer = window.setTimeout(() => { if (!$('coach').hidden && $('coach').dataset.tip === id) dismissCoach(false); }, COACH_AUTO_MS);
}
const COACH_AUTO_MS = params.get('coachAuto') === '0' ? 0 : 5000; // 测试量提示条位置时用 ?coachAuto=0 关掉自动收起（自动收起另有用例）
let coachTimer = 0;
/** 提示指向的位置：CSS 选择器，或 @bomb（手里第一组炸弹 / 四大天王的几张牌合起来的范围）。 */
function tipTarget(q: string): DOMRect | null {
  if (q === '@bomb') {
    const g = arrangeCombos(m.round.hands[ME], m.round.level).find((x) => x.label === '炸弹' || x.label === '四大天王');
    const els = g ? g.cards.map((c) => document.querySelector(`#hand .card[data-id="${c.id}"]`)).filter((e): e is HTMLElement => !!e) : [];
    if (!els.length) return null;
    const rs = els.map((e) => e.getBoundingClientRect());
    const l = Math.min(...rs.map((r) => r.left)), t = Math.min(...rs.map((r) => r.top)), r = Math.max(...rs.map((x) => x.right)), b = Math.max(...rs.map((x) => x.bottom));
    return new DOMRect(l, t, r - l, b - t);
  }
  const el = document.querySelector<HTMLElement>(q);
  if (!el || el.offsetParent === null) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 ? r : null;
}
/**
 * 把提示条放到目标旁边：在上、下、左、右四个位置里挑一个不挡出牌区、手牌、头像和按钮的（玩家之眼：报牌提示挡住了对手刚出的牌）；
 * 都会挡就挑挡得最少的。左右夹在屏幕内，尖角对准目标。手机横竖屏同一套规则。
 */
function placeCoach(): void {
  const c = $('coach'), spot = $('coachSpot');
  if (c.hidden) return;
  const id = c.dataset.tip ?? '';
  const t = TIPS[id]?.at.map(tipTarget).find((r): r is DOMRect => !!r);
  if (!t) { spot.hidden = true; c.style.left = '50%'; c.style.top = '30%'; c.style.setProperty('--ax', '50%'); c.dataset.side = ''; return; }
  spot.hidden = true; // 作者 11-03：目标外不套金色光环（影响美观），靠提示条尖角指向；位置仍照算，留作以后要用
  const tEl = TIPS[id].at[0].startsWith('@') ? null : document.querySelector<HTMLElement>(TIPS[id].at[0]);
  const rad = tEl ? Math.min(parseFloat(getComputedStyle(tEl).borderTopLeftRadius) || 8, t.height / 2) + 5 : 12; // 光环跟着按钮的圆角走
  Object.assign(spot.style, { left: `${t.left - 5}px`, top: `${t.top - 5}px`, width: `${t.width + 10}px`, height: `${t.height + 10}px`, borderRadius: `${rad}px` });
  c.style.maxWidth = ''; // 每次从原始宽度量起（收窄只在本次兜底里用，避免定时器反复收窄越缩越窄）
  const vw = innerWidth, vh = innerHeight, pad = 8, gap = 14, w = c.offsetWidth, h = c.offsetHeight;
  const KEEP = compact.matches ? 6 : 10;
  const clampX = (x: number) => Math.min(Math.max(x, pad), vw - w - pad);
  const clampY = (y: number) => Math.min(Math.max(y, pad), vh - h - pad);
  const cx = t.left + t.width / 2, cy = t.top + t.height / 2;
  const cands = [
    { side: 'above', x: clampX(cx - w / 2), y: t.top - gap - h },
    { side: 'below', x: clampX(cx - w / 2), y: t.bottom + gap },
    { side: 'left', x: t.left - gap - w, y: clampY(cy - h / 2) },
    { side: 'right', x: t.right + gap, y: clampY(cy - h / 2) },
  ].filter((k) => k.x >= pad - 1 && k.y >= pad - 1 && k.x + w <= vw - pad + 1 && k.y + h <= vh - pad + 1);
  const order = cy > vh / 2 ? ['above', 'left', 'right', 'below'] : ['below', 'left', 'right', 'above']; // 挡得一样少时按这个顺序选
  cands.sort((a, b) => order.indexOf(a.side) - order.indexOf(b.side));
  const obstacles = [...document.querySelectorAll<HTMLElement>('.played .mini, .played .card, #hand .card, #hand .col-tag, .seat, .bar .btn, .sort .btn, .top, #msg:not(:empty), #note:not(:empty), .narration')]
    .map((e) => e.getBoundingClientRect())
    .filter((r) => r.width > 0 && !(r.left >= t.left - 1 && r.right <= t.right + 1 && r.top >= t.top - 1 && r.bottom <= t.bottom + 1)) // 目标自己不算
    .map((r) => new DOMRect(r.left - KEEP, r.top - KEEP, r.width + 2 * KEEP, r.height + 2 * KEEP)); // 作者 11-03：提示条别贴着牌和按钮，四周留 KEEP 的空
  const cover = (k: { x: number; y: number }) => obstacles.reduce((n, r) => n + Math.max(0, Math.min(k.x + w, r.right) - Math.max(k.x, r.left)) * Math.max(0, Math.min(k.y + h, r.bottom) - Math.max(k.y, r.top)), 0);
  let best = cands.length ? cands.reduce((a, b) => (cover(b) < cover(a) ? b : a)) : { side: cy > vh / 2 ? 'above' : 'below', x: clampX(cx - w / 2), y: clampY(cy > vh / 2 ? t.top - gap - h : t.bottom + gap) };
  if (cover(best) > 0) {
    // 四个方向都会挡（小屏手机常见）：在整屏里找一块完全空着、离目标最近的地方放，不画尖角，靠目标上的金色光环指示
    // （作者 11-02：提示条挡住手牌不能接受）。目标自己也算障碍，不能盖住它。
    // 加权：手牌与目标绝对不挡（作者 11-02：挡住手牌不能接受），出的牌、头像、按钮在实在没有空位时才允许盖一点
    const hand0 = [...document.querySelectorAll<HTMLElement>('#hand .card, #hand .col-tag')].map((e) => e.getBoundingClientRect()).filter((r) => r.width > 0);
    let hard = hand0.map((r) => new DOMRect(r.left - KEEP, r.top - KEEP, r.width + 2 * KEEP, r.height + 2 * KEEP)); // 牌型签和手牌一样不能挡（作者 11-03）
    hard.push(new DOMRect(t.left - 6, t.top - 6, t.width + 12, t.height + 12));
    const area = (rs: DOMRect[], x: number, y: number, ww: number, hh: number) => rs.reduce((n, r) => n + Math.max(0, Math.min(x + ww, r.right) - Math.max(x, r.left)) * Math.max(0, Math.min(y + hh, r.bottom) - Math.max(y, r.top)), 0);
    const scan = (ww: number, hh: number) => {
      let pick: { side: string; x: number; y: number; soft: number } | null = null, dist = Infinity;
      for (let y = pad; y + hh <= vh - pad; y += 4) for (let x = pad; x + ww <= vw - pad; x += 4) {
        if (area(hard, x, y, ww, hh) > 0) continue;
        const soft = area(obstacles, x, y, ww, hh);
        const d = Math.hypot(x + ww / 2 - cx, y + hh / 2 - cy);
        if (!pick || soft < pick.soft || (soft === pick.soft && d < dist)) { pick = { side: 'free', x, y, soft }; dist = d; }
      }
      return pick;
    };
    let pick = scan(w, h);
    if (!pick) { hard = [...hand0, hard.at(-1)!]; pick = scan(w, h); } // 小屏留了空就放不下：退回只要求不压住（目标光环仍是障碍）
    if (!pick || pick.soft > 0) { // 收窄到七成、多折一行再找一遍，取更好的
      c.style.maxWidth = `${Math.round(w * 0.7)}px`;
      const narrow = scan(c.offsetWidth, c.offsetHeight);
      if (narrow && (!pick || narrow.soft < pick.soft)) pick = narrow; else c.style.maxWidth = '';
    }
    c.dataset.free = !pick ? 'none' : pick.soft === 0 ? 'yes' : 'partial';
    if (pick) best = pick;
  } else c.dataset.free = '';
  c.dataset.side = best.side;
  c.style.left = `${best.x}px`;
  c.style.top = `${best.y}px`;
  c.style.setProperty('--ax', `${Math.min(Math.max(cx - best.x, 24), w - 24)}px`);
  c.style.setProperty('--ay', `${Math.min(Math.max(cy - best.y, 18), h - 18)}px`);
}
addEventListener('resize', placeCoach);
setInterval(placeCoach, 400); // 目标会随出牌、理牌移动，跟着走
function dismissCoach(clicked: boolean): void {
  window.clearTimeout(coachTimer);
  tipsDone.add($('coach').dataset.tip ?? '');
  put('tips', [...tipsDone]);
  show('coach', false);
  show('coachSpot', false);
  if (clicked) sfx('button');
  flushTips();
}
btn('coachOk').onclick = () => dismissCoach(true);
/** 讲手牌的提示只在轮到我时弹（一手一条）；报牌、进贡、升级这类事件提示在事情发生时弹。 */
const HAND_TIPS = new Set(['play', 'sort', 'wild', 'bomb']);
function flushTips(): void {
  // 只把当前队列过一遍：tip() 可能把提示放回队列（这一手已经弹过一条），不能用 while 一直取，否则死循环（玩家之眼回归时复现）
  const mine = m.round.turn === ME && !m.round.over;
  for (const id of tipQueue.splice(0)) {
    if (tipsDone.has(id)) continue;
    if (paused() || !$('coach').hidden || (HAND_TIPS.has(id) && !mine)) tipQueue.push(id); else tip(id);
  }
}

// ── 桌面 ──
function playedEl(p: Play | null): HTMLElement[] {
  if (p === null) return []; // 不出不写字（作者 11-02：用语音播报代替）
  // 牌旁不再写牌型（作者 11-02：牌放大，文字不需要，有语音播报）；牌型名留给读屏与悬停
  const mini = Object.assign(document.createElement('div'), { title: PLAY_NAME[p.type] });
  mini.setAttribute('aria-label', PLAY_NAME[p.type]);
  mini.className = `mini${isBombType(p.type) ? ' bombfx' : ''}`;
  mini.append(...sortHand(p.cards, m.round.level).map((c) => cardEl(c, m.round.level)));
  return [mini];
}

function track(id: string, lv: number): void {
  $(id).replaceChildren(...Array.from({ length: 13 }, (_, i) => Object.assign(document.createElement('i'), { className: i + 2 <= lv ? 'on' : '', title: STATIONS[i + 2] })));
}

/** 对家出的牌放在对家头像下方、留 12px（作者 11-02：牌跟头像挤在一起）；按实际位置算，屏幕尺寸、头像大小变了也不会挤。 */
let topPlayKey = '';
function placeTopPlay(): void {
  // 性能（作者 11-03 优化卡顿）：位置只跟屏幕尺寸和三个座位（名字、名次、报牌签会改变座位大小）有关，没变就不重算，免得每次重画都逼浏览器重新排版
  const key = `${innerWidth}x${innerHeight}|${[1, 2, 3].map((x) => $(`s${x}`).textContent).join('|')}`;
  if (key === topPlayKey) return;
  topPlayKey = key;
  const t = table.getBoundingClientRect(), s = $('s2').getBoundingClientRect();
  const p2 = $('play2');
  if (matchMedia('(orientation: landscape) and (max-height: 500px)').matches) {
    // 手机横屏（v0.8）：队友出的牌放在队友头像右边，牌桌下方正中留给自己出的牌
    Object.assign(p2.style, { top: `${Math.round(s.top - t.top)}px`, left: `${Math.round(s.right - t.left + 8)}px`, transform: 'none' });
  } else Object.assign(p2.style, { top: `${Math.round(s.bottom - t.top + 12)}px`, left: '', transform: '' });
  // 两侧：默认按样式表的位置；小屏上会顶到头像时，往下挪到头像下方 10px
  for (const seat of [1, 3]) {
    const box = $(`play${seat}`), av = $(`s${seat}`).getBoundingClientRect();
    box.style.top = '';
    const top = box.getBoundingClientRect().top;
    if (top < av.bottom + 10) box.style.top = `${Math.round(av.bottom - t.top + 10)}px`;
  }
}
addEventListener('resize', () => { topPlayKey = ''; placeTopPlay(); });
function render(): void {
  placeTopPlay();
  const r = m.round;
  $('roundNo').textContent = `第 ${m.roundNo} 副${m.rules.roundLimit ? ` / ${m.rules.roundLimit}` : ''}`;
  $('lvUsBig').textContent = rankName(m.teamLevel[0]);
  $('lvThemBig').textContent = rankName(m.teamLevel[1]);
  $('lvbUs').classList.toggle('playing', r.levelTeam === 0);
  $('lvbThem').classList.toggle('playing', r.levelTeam === 1);
  $('lvGoal').textContent = m.rules.roundLimit ? `限${m.rules.roundLimit}副` : `过${rankName(m.rules.target ?? 14)}`;
  $('lvBox').title = `这一副打${rankName(r.level)}（${r.levelTeam === 0 ? '我方' : '对方'}的级）；${m.rules.roundLimit ? `打满 ${m.rules.roundLimit} 副比级数` : `先过 ${rankName(m.rules.target ?? 14)} 的一方赢`}`;
  $('lvUs').textContent = rankName(m.teamLevel[0]);
  $('lvThem').textContent = rankName(m.teamLevel[1]);
  $('stnUs').textContent = `·${stationOf(m.teamLevel[0])}`;
  $('stnThem').textContent = `·${stationOf(m.teamLevel[1])}`;
  track('trackUs', m.teamLevel[0]);
  track('trackThem', m.teamLevel[1]);
  for (const s of [1, 2, 3] as Seat[]) {
    const p = personaOf(s)!;
    const done = r.finished.indexOf(s);
    const n = visibleCount(m, s); // 报牌：超过 10 张不公开
    const av = $(`av${s}`);
    if (av.dataset.id !== p.id) { av.dataset.id = p.id; av.style.setProperty('--tint', p.tint); av.innerHTML = avatarInner(p.id, p.avatar); }
    av.dataset.role = s === 2 ? '队友' : '对手'; // 头像上直接标队友 / 对手（参考腾讯，比颜色环更直白）
    const os = online?.seats[s];
    const tag = os?.human ? `<span class="badge friend">${os.online ? '好友' : '掉线·托管'}</span>` : '';
    const jh = os?.human ? os.title ?? '' : p.title; // 江湖名号在昵称下方（作者 11-03）；联机真人显示他自己的名号
    if ($(`jh${s}`).textContent !== jh) $(`jh${s}`).textContent = jh;
    $(`who${s}`).innerHTML = `${p.name}${tag}${done >= 0 ? `<span class="badge place">${PLACE[done]}</span>` : n !== null ? `<span class="badge">报牌 ${n}</span>` : ''}`;
    $(`seat${s}`).textContent = r.turn === s && !r.over ? '出牌中…' : ''; // 作者 11-03：去掉“多于 10 张”；剩 10 张以内仍按规则报牌（头像旁“报牌 N”徽标）
    $(`s${s}`).classList.toggle('turn', r.turn === s && !r.over);
  }
  for (const s of [0, 1, 2, 3] as Seat[]) {
    // 内容没变就不重画，免得把正在播的“掼”动画替换掉
    const shown = lastShown.get(s);
    const key = !lastShown.has(s) ? '' : shown === null ? 'pass' : `${r.level}:${shown!.cards.map((c) => c.id).join(',')}`;
    const box = $(`play${s}`);
    if (box.dataset.key !== key) { box.dataset.key = key; box.replaceChildren(...(lastShown.has(s) ? playedEl(shown!) : [])); }
  }
  renderHand();
  const myTurn = r.turn === ME && !r.over && !r.finished.includes(ME);
  btn('btnPlay').disabled = !myTurn;
  btn('btnPass').disabled = !myTurn || !target(r);
  btn('btnHint').disabled = !myTurn;
  document.body.classList.toggle('myturn', myTurn); // 手机横屏：轮到我才显示出牌按钮，不轮到我时这一行放我出的牌（参考腾讯）
  // 压不过（作者 11-02 参考腾讯“没有牌大过上家”）：“不出”变成醒目的主按钮“要不起”，出牌退成次按钮；出牌仍可点，以防判断漏了某种出法
  const t = target(r);
  const noBeat = myTurn && !!t && !candidates(r.hands[ME], r.level).some((p) => beats(p, t));
  btn('btnPass').textContent = noBeat ? '要不起' : '不出';
  btn('btnPass').classList.toggle('main', noBeat);
  btn('btnPass').classList.toggle('nobeat', noBeat);
  btn('btnPlay').classList.toggle('main', !noBeat);
  if (noBeat && !$('msg').textContent) $('msg').textContent = '没有牌大过上家';
  document.querySelector('.msgline')!.classList.toggle('nobeat', noBeat && $('msg').textContent === '没有牌大过上家');
  btn('btnSortRank').classList.toggle('on', sortMode === 'rank');
  btn('btnSortCombo').classList.toggle('on', sortMode === 'combo');
  const narr = table.querySelector<HTMLElement>('.narration');
  if (narr) placeNarration(narr); // 开场卷轴同理：轮到你、按钮行冒出来时重新找空当
  placeCoach(); // 桌面一变（有人出牌、理牌）就重新给提示条选位，不等定时器，免得刚出的牌落在提示条下面
}

const clearChoices = () => $('choices').replaceChildren();

/** 出一手（人或机器人）后：记桌面、放特效与声音、牌友搭话、教学提示。before 取自 act 之前。 */
function after(seat: Seat, play: Play | null, before: { leading: boolean; owner: Seat | null; finished: number; counts: (number | null)[] }): void {
  const r = m.round;
  if (before.leading) lastShown.clear();
  lastShown.set(seat, play);
  render();
  if (play) {
    playFx(seat, play);
    announce(play);
    if (isBombType(play.type)) {
      if (seat === ME) stats.bombs++;
      if (seat === ME && (play.type === 'bomb' || play.type === 'straightFlush') && play.cards.some((c) => isWild(c, r.level))) stats.wild = (stats.wild ?? 0) + 1;
    }
    if (r.finished.length > before.finished && r.finished[0] === seat && before.finished === 0) {
      firstFx(seat);
      call('first');
      if (seat === ME) stats.firsts++;
    }
    const n = visibleCount(m, seat);
    const left = r.hands[seat].length;
    if (left > 0 && left <= 2 && (before.counts[seat] === null || (before.counts[seat] ?? 99) > 2)) { call('lastCards'); lastFx(seat, left); }
    else if (n !== null && before.counts[seat] === null) { sfx('report'); call('report'); reportFx(seat, n); if (seat % 2 === 1) tip('report'); }
  } else { sfx('pass'); call('pass'); passFx(seat); }
  // 借风：一圈结束，领出的是“出完的人”的搭档
  if (!r.over && !r.last && before.owner !== null && r.finished.includes(before.owner) && r.lead === partnerOf(before.owner)) {
    windFx(before.owner, r.lead);
    call('borrowWind');
  }
  save();
}
const snapshot = () => ({ leading: !target(m.round), owner: m.round.last?.seat ?? null, finished: m.round.finished.length, counts: [0, 1, 2, 3].map((s) => visibleCount(m, s as Seat)) });

function step(): void {
  clearTimeout(timer);
  render();
  flushTips();
  const r = m.round;
  // 一副结束：最后一手在桌上停一会儿、它的播报（如“炸弹！”）说完再弹结算（作者 11-02：播报炸弹时结算已经跳出来了）
  if (r.over) { timer = window.setTimeout(() => void voice.idle(3500).then(showResult), settings.fx ? 1400 : 0); return; }
  clearTimeout(urgeTimer);
  if (r.turn === ME) {
    sfx('myTurn');
    armUrge();
    const hand = r.hands[ME];
    tip('play');
    if (hand.length > 20) tip('sort');
    if (hand.some((c) => isWild(c, r.level))) tip('wild');
    if (arrangeCombos(hand, r.level).some((g) => g.label === '炸弹' || g.label === '四大天王')) tip('bomb');
    return;
  }
  if (online) return; // 联机：别人的出牌由服务端推过来
  const run = () => {
    if (paused() || performance.now() < narrUntil) { timer = window.setTimeout(run, 250); return; } // 首页或弹层开着、开场白没播完时暂停
    const seat = r.turn;
    const before = snapshot();
    const play = act(m, seat, choose(viewOf(m, seat), personaOf(seat)?.style, stats.level));
    after(seat, play, before);
    step();
  };
  // 等别人出牌时偶尔闲聊一句（概率与冷却见语音体系），说了就多等一会儿再出，别让播报盖住闲话
  const chatted = !testMode && !talking.has(r.turn) && speak(r.turn, 'idle', 1);
  timer = window.setTimeout(run, BOT_DELAY_MS + (chatted ? 1400 : 0));
}

function humanAct(cards: Card[], as?: Play): void {
  if (online) {
    // 先在本地副本上校验（只用到自己的牌和桌面），合规才发给服务端；服务端再判一次，以服务端为准
    try { act(structuredClone(m), ME, cards, as && { type: as.type, key: as.key }); } catch (e) { sfx('error'); $('msg').textContent = (e as Error).message; return; }
    for (const id of ['btnPlay', 'btnPass', 'btnHint']) btn(id).disabled = true;
    netSend({ t: 'play', ids: cards.map((c) => c.id), as: as && { type: as.type, key: as.key } });
    return;
  }
  try {
    const before = snapshot();
    const play = act(m, ME, cards, as && { type: as.type, key: as.key });
    selected.clear();
    $('msg').textContent = '';
    btn('btnPlay').classList.remove('invalid');
    clearChoices();
    after(ME, play, before);
    step();
  } catch (e) {
    sfx('error');
    $('msg').textContent = (e as Error).message;
  }
}

/** 出法按钮上的名字：连牌写范围（顺子 7-J），同点类写点数（五炸 8、三带对·三个 K）。key：连牌是最高张的位置（A2345 为 5），其余是牌力（级牌 = 15）。 */
function playLabel(p: Play): string {
  const lv = m.round.level;
  const rk = (k: number) => rankName(k === 15 ? lv : k === 1 ? 14 : k);
  const runLen: Partial<Record<Play['type'], number>> = { straight: 5, straightFlush: 5, tube: 3, plate: 2 };
  const len = runLen[p.type];
  if (len) return `${PLAY_NAME[p.type]} ${rk(p.key - len + 1)}-${rk(p.key)}`;
  if (p.type === 'bomb') return `${CN[p.size] ?? p.size}炸 ${rk(p.key)}`;
  if (p.type === 'fullHouse') return `三带对·三个 ${rk(p.key)}`;
  if (p.type === 'jokerBomb') return PLAY_NAME[p.type];
  return `${PLAY_NAME[p.type]} ${p.key >= 16 ? (p.key === 17 ? '大王' : '小王') : rk(p.key)}`;
}
btn('btnPlay').onclick = () => {
  const cards = m.round.hands[ME].filter((c) => selected.has(c.id));
  // 没选牌不能当成“不出”：不出要点“不出”按钮（审计 P1-1）
  if (!cards.length) { sfx('error'); $('msg').textContent = '先选要出的牌；不出请点“不出”'; return; }
  if (!checkSelection()) { sfx('error'); return; } // 实时校验不通过：只提示，不出牌
  const options = legalPlays(cards, m.round.level, target(m.round));
  if (options.length <= 1) { humanAct(cards); return; }
  // 一手牌有多种合法出法（两对加两张逢人配：三连对或三同连张；同花顺：当顺子或当炸弹）时让玩家选
  $('msg').textContent = '这手牌有几种出法：';
  $('choices').replaceChildren(...options.map((p) => {
    const b = document.createElement('button');
    b.textContent = `按${playLabel(p)}出`; // 作者 11-03：两个“按顺子出”分不清——写上点数范围
    b.onclick = () => humanAct(cards, p);
    return b;
  }));
};
btn('btnPass').onclick = () => humanAct([]);
btn('btnHint').onclick = () => {
  selected.clear();
  for (const c of choose(viewOf(m, ME), undefined, 'normal')) selected.add(c.id);
  clearChoices(); handKey = ''; render(); sfx('button');
  // 建议不出时要说出来，不然看起来像按钮没反应（实打复现）
  if (!selected.size) {
    const owner = m.round.last?.seat;
    $('msg').textContent = owner !== undefined && owner === partnerOf(ME) ? '搭档的牌，建议不压，点「不出」' : '没有压得住的牌（或不值得用炸弹），建议点「不出」';
  } else checkSelection();
};
btn('btnSortRank').onclick = () => { resetSelection(); sortMode = 'rank'; handKey = ''; render(); sfx('button'); };
/** 理牌时把已选的牌全部放下、清掉出法提示（作者 11-03：选了牌想换组合，一张张撤回太麻烦，点理牌就回到未选状态） */
const resetSelection = () => { selected.clear(); clearChoices(); checkSelection(); if (/^(可以出|压不住|不成牌型|这手牌有几种出法)/.test($('msg').textContent ?? '')) $('msg').textContent = ''; };
btn('btnSortCombo').onclick = () => { resetSelection(); sortMode = 'combo'; planIdx = 0; handKey = ''; $('hand').classList.add('regroup'); render(); sfx('button'); }; // 再点一次 = 全量重算、回到最优方案（规格第四节第 3 条）；回按点数排点“按点数”

// ── 一副结算（③ 江湖路：山路图与走旗动画，src/routeMap.ts） ──
/** 最后一副之前两队的级数（按每副结果重放），过关动画的出发站。 */
function levelsBefore(): [number, number] {
  const lv: [number, number] = [2, 2];
  for (const res of m.results.slice(0, -1)) if (!res.passedA) lv[res.winnerTeam] = Math.min(14, lv[res.winnerTeam] + res.steps);
  return lv;
}
function showResult(): void {
  stats.behind = Math.max(stats.behind ?? 0, m.teamLevel[1] - m.teamLevel[0]); // 落后几级（绝地翻盘）
  if (!m.round.over || !$('ovResult').hidden || !$('ovFinal').hidden) return;
  const res = m.results.at(-1)!;
  if (m.winner !== null) {
    if (online?.phase === 'settling') {
      $('msg').textContent = '正在结算，入账前不会显示赛果';
      show('btnRetrySettle', online.host);
      return;
    }
    showFinal(); return;
  }
  sfx(res.winnerTeam === 0 ? 'roundWin' : 'roundLose');
  sound.music.switchTo('result');
  call(res.winnerTeam === 0 ? 'roundUs' : 'roundThem');
  // 标题直接写谁赢（作者 11-02：手机上看不出我方还是对方赢了）
  const us = res.winnerTeam === 0;
  $('resultTitle').innerHTML = `第 ${m.roundNo} 副 · <span class="verdict ${us ? 'us' : 'them'}">${us ? '我方胜' : '对方胜'}</span>`;
  $('resultTitle').classList.remove('gold');
  $('resultTeams').replaceChildren(...([0, 1] as const).map((team) => teamCard(team, res.order, res.winnerTeam === team ? res.steps : 0)));
  $('resultLine').textContent = `下一副打 ${rankName(m.teamLevel[res.winnerTeam])}（${res.winnerTeam === 0 ? '我方' : '对方'}的级）`;
  btn('btnNext').textContent = '下一副';
  btn('btnNext').disabled = false;
  show('ovResult');
  // 过关动画：两队的旗沿山路从上一站走到新站（关掉“牌桌动画”时直接停在新站）
  const before = levelsBefore();
  void drawRouteMap($('route'), before, [m.teamLevel[0], m.teamLevel[1]], settings.fx);
  tip('level');
}

/** 一副结算的队伍卡：队名、两人（头像 + 名字 + 名次）、升了几级到哪一站。我方黛绿、对方朱红，和航线图船旗同色。 */
function teamCard(team: 0 | 1, order: Seat[], steps: number): HTMLElement {
  const box = Object.assign(document.createElement('div'), { className: `team-card ${team === 0 ? 'us' : 'them'}${steps ? ' up' : ''}` });
  const seats = (team === 0 ? [0, 2] : [1, 3]) as Seat[];
  const rows = seats.sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((s) => {
    const c = charOf(s)!;
    const place = order.indexOf(s);
    return `<li>${avatarHtml(c.id, c.avatar)}<b>${s === ME ? '我' : c.name}</b><span class="place p${place}">${PLACE[place]}</span></li>`;
  }).join('');
  const lv = m.teamLevel[team];
  box.innerHTML = `${steps ? '<i class="win-seal" aria-label="本副胜">胜</i><i class="win-glow" aria-hidden="true"></i>' : ''}<h4>${team === 0 ? '我方' : '对方'}</h4><ul>${rows}</ul>`
    + `<p class="gain">${steps ? `升 <b>${steps}</b> 级 · 到${stationOf(lv)}` : `原地 · ${stationOf(lv)}`}</p>`;
  return box;
}

/** 开下一副。我收到进贡时，按竞赛规则第十条由我自己选还哪张。 */
function startNext(): void {
  if (online) { netSend({ t: 'next' }); btn('btnNext').disabled = true; btn('btnNext').textContent = '等其他玩家…'; return; }
  show('ovResult', false);
  sound.music.switchTo('table');
  // 先在副本上试算（发牌按种子固定，试算与正式结果一致），看我要不要还牌、能还哪些
  let myOptions: Card[] = [];
  const trial = structuredClone(m);
  nextRound(trial, (seat, options) => { if (seat === ME) myOptions = options; return defaultReturn(options, trial.teamLevel[trial.results.at(-1)!.winnerTeam]); });
  const mine = trial.round.tributes.find((t) => t.to === ME);
  if (!mine || !myOptions.length) { commitNext(); return; }
  const level = trial.round.level;
  $('returnInfo').textContent = `${NAMES[mine.from]}进贡给你 ${cardName(mine.card)}，选一张还给${NAMES[mine.from]}：`;
  $('returnPick').replaceChildren(...sortHand(myOptions, level).reverse().map((c) => {
    const el = cardEl(c, level, 'button');
    el.onclick = () => { show('ovReturn', false); commitNext(c.id); };
    return el;
  }));
  show('ovReturn');
}

function commitNext(myBack?: number): void {
  const level = m.teamLevel[m.results.at(-1)!.winnerTeam];
  nextRound(m, (seat, options) => (seat === ME && myBack !== undefined ? options.find((c) => c.id === myBack)! : defaultReturn(options, level)));
  if (m.round.tributes.length) sfx('tribute');
  resetTable();
  if (m.round.tributes.length || m.round.resisted) tip('tribute');
}
btn('btnNext').onclick = startNext;

// ── 战绩榜（比赛结束）与生涯战绩 ──
const fmtTime = (ms: number) => { const s = Math.round(ms / 1000); return `${Math.floor(s / 60)}分${s % 60}秒`; }; // 不加空格：结算页 6 格一行时不折行
function statBox(label: string, value: string): HTMLElement {
  const d = document.createElement('div');
  d.innerHTML = `<b>${value}</b><span>${label}</span>`;
  return d;
}
function showFinal(): void {
  if (online && online.phase !== 'final') return;
  if (!$('ovFinal').hidden && $('ovFinal').dataset.mode === 'match') return;
  finalMode = 'match';
  careerFrom = 'home'; show('btnFinalAgain', true);
  show('profile', false);
  $('ovFinal').dataset.mode = 'match';
  show('careerProgress', false);
  $('careerHead').textContent = '生涯战绩';
  const won = m.winner === 0;
  sfx(won ? 'passA' : 'roundLose');
  const fullA = (m.rules.target ?? 14) === 14; // 短局（过 6 / 过 10）赢了不报“恭喜过 A”
  if (!won || fullA) call(won ? 'matchWin' : 'matchLose');
  sound.music.switchTo('champion');
  let promoted = '';
  if (!online && (!testMode || params.get('career') === '1')) {
    const c = career();
    const before = titleIndex(c);
    c.matches++; c.rounds += m.results.length; c.firsts += stats.firsts; c.bombs += stats.bombs;
    c.wildCombos += stats.wild ?? 0;
    c.farthest = Math.max(c.farthest, Math.min(14, m.teamLevel[0]));
    if (won) { c.wins++; c.streak++; c.best = Math.max(c.best, c.streak); } else c.streak = 0;
    if (won && fullA && m.results.at(-1)!.passedA) { c.golds++; c.farthest = 14; } // 问鼎武林：我方真打过 A（短局过 6 / 过 10 不算）
    if (won && (stats.behind ?? 0) >= 5) c.comebacks++;
    put('career', c);
    const after = titleIndex(c);
    if (after > before) { promoted = TITLES[after].name; void voice.queue(NARRATOR.id, `promote_${after}.mp3`); } // 播报员在“比赛胜利”之后念晋升（作者 11-03）
  }
  showPromotion(promoted);
  save();
  const byA = m.results.at(-1)!.passedA;
  $('finalTitle').textContent = won ? (fullA ? '问鼎武林' : '旗开得胜') : '铩羽而归'; // 作者 11-03：武侠江湖主题；短局赢了是“旗开得胜”
  $('finalTitle').classList.toggle('gold', won);
  $('finalSub').textContent = `${won ? '我方' : '对方'}${byA ? `过 ${rankName(m.rules.target ?? 14)}` : `打满 ${m.rules.roundLimit} 副，级数领先`} · ${online ? '好友联机 · 结算已入账' : `${DIFFICULTY_NAME[stats.level]}难度`} · 搭档${NAMES[2]}`;
  $('finalStats').replaceChildren(
    statBox('结果', won ? '胜' : '负'),
    statBox('打了', `${m.results.length} 副`),
    statBox('用时', fmtTime(Date.now() - stats.start)),
    statBox('我拿上游', `${stats.firsts} 次`),
    statBox('我出炸弹', `${stats.bombs} 次`),
    statBox("级数", `${rankName(m.teamLevel[0])} : ${rankName(m.teamLevel[1])}`),
  );
  renderHistory('finalRounds');
  $('finalRounds').closest('table')!.hidden = false;
  if (online) {
    const finishedRoom = online;
    $('careerHead').textContent = '联机生涯战绩';
    $('careerStats').replaceChildren();
    void fetch('/api/me/career', { credentials: 'include' }).then(async (response) => {
      if (!response.ok) throw new Error('联机战绩暂不可读');
      const c = await response.json() as { matches: number; wins: number; rounds: number; firsts: number };
      if (online !== finishedRoom) return;
      $('careerStats').replaceChildren(statBox('总场次', String(c.matches)), statBox('胜场', String(c.wins)),
        statBox('累计副数', String(c.rounds)), statBox('拿上游', String(c.firsts)));
    }).catch(() => { if (online === finishedRoom) $('careerHead').textContent = '结算已入账，联机战绩暂不可读'; });
  } else renderCareer();
  btn('btnFinalClose').textContent = '看看牌桌';
  btn('btnFinalAgain').textContent = '再闯一次江湖';
  show('ovFinal');
}
/** 每副记录的表格行（战绩金榜与算分面板共用 roundHistory）。 */
/** 手机（横屏）上表格只写上游是谁（双下加注），一行写完；电脑写完整名次。 */
const compactTable = matchMedia('(max-height: 500px), (max-width: 900px)');
function historyRows(): HTMLElement[] {
  return roundHistory(m).map((h) => {
    const tr = document.createElement('tr');
    tr.className = h.winnerTeam === 0 ? 'us' : 'them';
    const o = h.order as Seat[];
    const places = compactTable.matches ? `上游 ${NAMES[o[0]]}${o[1] % 2 === o[0] % 2 ? '（双下）' : ''}` : placeLines(o).join('，');
    tr.innerHTML = `<td>${h.no}</td><td>${rankName(h.playing)}</td><td>${places}</td><td>${h.passedA ? '过 A' : `${h.winnerTeam === 0 ? '我方' : '对方'} +${h.steps}`}</td>`;
    return tr;
  });
}

/**
 * 每副记录表：超过 4 副时只显示最近 4 副，末尾一行“展开全部（共 N 副）”，点了才显示全部
 * （作者 11-02：手机上打满 8 副要滑动，加“展开全部”）。终局与算分面板共用。
 */
function renderHistory(tbodyId: string): void {
  const rows = historyRows();
  const body = $(tbodyId);
  if (rows.length <= 4) { body.replaceChildren(...rows); return; }
  const more = document.createElement('tr');
  more.className = 'more';
  more.innerHTML = `<td colspan="4"><button class="btn small" type="button">展开全部（共 ${rows.length} 副）</button></td>`;
  more.querySelector('button')!.addEventListener('click', () => { sfx('button'); body.replaceChildren(...historyRows()); layoutPanels(); });
  body.replaceChildren(...rows.slice(-4), more);
}

// ── 算分面板（玩家反馈：自己算不清楚）：点顶栏比分打开 ──
function openScore(): void {
  const r = m.round;
  const lim = m.rules.roundLimit;
  const lead = m.teamLevel[0] === m.teamLevel[1] ? '持平' : m.teamLevel[0] > m.teamLevel[1] ? '我方领先' : '对方领先';
  $('scoreNow').replaceChildren(
    statBox(`我方 · ${stationOf(m.teamLevel[0])}`, rankName(m.teamLevel[0])),
    statBox(`对方 · ${stationOf(m.teamLevel[1])}`, rankName(m.teamLevel[1])),
    statBox(`本副打（${r.levelTeam === 0 ? '我方' : '对方'}的级）`, rankName(r.level)),
    statBox(lim ? `限 ${lim} 副，${lead}` : `赛制：打到过 ${rankName(m.rules.target ?? 14)}`, lim ? `剩 ${Math.max(0, lim - m.results.length - (r.over ? 0 : 1))} 副` : `第 ${m.roundNo} 副`),
  );
  const atA = ([0, 1] as const).filter((t) => m.teamLevel[t] === 14).map((t) => (t === 0 ? '我方' : '对方'));
  $('scoreRule').textContent = `只有上游一方升级：搭档二游升 3 级、三游升 2 级、下游升 1 级。${atA.length ? `${atA.join('、')}已到 A：打自己的 A 时拿上游、且搭档不是下游，就过 A 赢下这一轮。` : '打到 A 后，打自己的 A 时拿上游、且搭档不是下游即过 A。'}${r.finished.length ? '下表标红的是按已出完的人还可能出现的结果。' : ''}`;
  const placeName = ['', '', '二游', '三游', '下游'];
  $('scoreOutcomes').replaceChildren(...outcomes(m).map((o) => {
    const tr = document.createElement('tr');
    tr.className = `${o.team === 0 ? 'us' : 'them'}${o.possible && r.finished.length ? ' likely' : ''}`;
    tr.innerHTML = `<td>${o.team === 0 ? '我方' : '对方'}</td><td>${placeName[o.partnerPlace]}</td><td>+${o.steps}</td><td>${o.passA ? '过 A，赢下这一轮' : `${rankName(o.from)} → ${rankName(o.to)}（${stationOf(o.to)}）`}</td>`;
    return tr;
  }));
  renderHistory('scoreHistory');
  if (!m.results.length) $('scoreHistory').innerHTML = '<tr><td colspan="4">还没有打完的副</td></tr>';
  show('ovScore');
  sfx('button');
}
btn('btnScore').onclick = openScore;
btn('btnCloseScore').onclick = () => show('ovScore', false);

/** 个人档案卡：头像、昵称（点一下改）、江湖名号与一句江湖话、离下一级的进度、已得的绰号。 */
function renderProfile(): void {
  const c = career();
  const t = TITLES[titleIndex(c)], nx = nextTitle(c);
  const ch = characterById(settings.me);
  const av = $('pfAv');
  av.style.setProperty('--tint', ch?.tint ?? '#e4ded2');
  av.innerHTML = avatarInner(settings.me, ch?.avatar ?? '我');
  $('pfName').textContent = myName();
  $('pfTitle').textContent = t.name;
  $('pfNicks').replaceChildren(...nicknamesOf(c).map((n) => Object.assign(document.createElement('i'), { textContent: n, title: NICKNAMES.find((x) => x.name === n)?.need ?? '' })));
  $('pfLine').textContent = `“${t.line}”`;
  ($('pfBar') as HTMLElement).style.width = nx ? `${Math.round((nx.have / nx.need) * 100)}%` : '100%';
  $('pfHint').textContent = `${nx ? nx.hint : '已是江湖最高名号'} · 最远到过${stationOf(c.farthest)}`;
}
btn('pfName').onclick = () => {
  const b = btn('pfName');
  const inp = Object.assign(document.createElement('input'), { value: myName(), maxLength: 8, className: 'pf-input', ariaLabel: '昵称' });
  b.replaceWith(inp); inp.focus(); inp.select();
  const done = () => {
    const v = inp.value.replace(/[<>&"'`]/g, '').trim().slice(0, 8);
    put('nick', v === (characterById(settings.me)?.name ?? '') ? '' : v); // 和角色名一样就不另存
    inp.replaceWith(b); renderProfile();
  };
  inp.onkeydown = (e) => { if (e.key === 'Enter') inp.blur(); if (e.key === 'Escape') { inp.value = myName(); inp.blur(); } };
  inp.onblur = done;
};
/** 晋升印（作者 11-03）：比赛结算时名号升级，盖一枚朱红印“晋升 · 名号”，配那句江湖话。 */
function showPromotion(name: string): void {
  const el = $('promo');
  el.hidden = !name;
  if (!name) return;
  const t = TITLES.find((x) => x.name === name)!;
  el.innerHTML = `<b class="pseal">晋升</b><span><em>${t.name}</em>${t.line}</span>`;
}

function renderCareer(): void {
  const c = career();
  $('careerStats').replaceChildren(
    statBox('总场次', String(c.matches)),
    statBox('胜率', c.matches ? `${Math.round((c.wins / c.matches) * 100)}%` : '—'),
    statBox('最长连胜', String(c.best)),
    statBox('上游率', c.rounds ? `${Math.round((c.firsts / c.rounds) * 100)}%` : '—'),
    statBox('累计炸弹', String(c.bombs)),
    statBox('问鼎', String(c.golds)), // 问鼎武林次数；标签短，小屏 6 格排一行
  );
}
function showCareer(): void {
  finalMode = 'career';
  renderCareer();
  renderProfile(); show('profile', true); show('promo', false);
  $('ovFinal').dataset.mode = 'career';
  const saved = savedMatch();
  $('finalTitle').textContent = '生涯战绩';
  $('finalTitle').classList.remove('gold');
  $('finalStats').replaceChildren();
  $('finalRounds').closest('table')!.hidden = true;
  $('careerHead').textContent = '总战绩';
  show('careerProgress', !!saved);
  if (saved) {
    // 现在在哪一站：山路图上两面旗停在双方当前的级（不播走旗动画），文字写第几副、打几、各到哪站
    const sm = saved.m;
    $('finalSub').textContent = `这一局打到第 ${sm.roundNo} 副 · 打 ${rankName(sm.round.level)}（${sm.round.levelTeam === 0 ? '我方' : '对方'}的级）`;
    $('careerWhere').innerHTML = `<b class="us">我方到${stationOf(sm.teamLevel[0])}</b>（${rankName(sm.teamLevel[0])}）　<b class="them">对方到${stationOf(sm.teamLevel[1])}</b>（${rankName(sm.teamLevel[1])}）　离论道台还差 ${Math.max(0, 14 - sm.teamLevel[0])} 级`;
    void drawRouteMap($('careerRoute'), [sm.teamLevel[0], sm.teamLevel[1]], [sm.teamLevel[0], sm.teamLevel[1]], false);
    btn('btnFinalAgain').textContent = sm.round.history.length ? `接着打第 ${sm.roundNo} 副` : `开始第 ${sm.roundNo} 副`;
  } else {
    $('finalSub').textContent = '本机记录 · 没有没打完的局';
    btn('btnFinalAgain').textContent = '开始新一局';
  }
  btn('btnFinalClose').textContent = careerFrom === 'menu' ? '回到牌桌' : '返回首页';
  if (careerFrom === 'menu') show('btnFinalAgain', false); else show('btnFinalAgain', true); // 从牌桌进来：只看档案，不另开一局
  show('ovFinal'); // 叠在首页上面（弹层层级高于首页），背后不露牌桌
}
btn('btnFinalAgain').onclick = () => {
  show('ovFinal', false);
  if (online) { leaveOnline(); openOnline(); return; }
  if (finalMode === 'career' && savedMatch()) { resumeSaved(); return; } // 生涯战绩页：从当前那一站开始下一局
  setupStep(1); openSetup();
};
btn('btnFinalClose').onclick = () => { show('ovFinal', false); if (finalMode === 'career' && careerFrom === 'home') openHome(); careerFrom = 'home'; };

// ── 好友联机：四人牌桌、房号加入、空位电脑补位；身份由服务端验证 ──
// 服务端发来的对局已经“遮挡 + 旋转”：我永远是 0 号座，别人的手牌是空白牌。牌桌界面与单机共用。
/**
 * 命令编号和旧单机游客存档用 UUID。crypto.randomUUID 只在 HTTPS 或本机地址下才有，用 http://IP:端口 访问时没有——
 * 直接调用会让整个页面起不来（11-02 公网部署首测复现，本机测试走 127.0.0.1 测不出），所以用 getRandomValues 兜底。
 */
const newGuestId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80; // UUID v4 格式
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};
// 旧游客 ID 只用于兼容本机存档，联机认证不接受客户端自报的 guest。
(() => { let g = store<string>('guest', ''); if (!/^[a-z0-9-]{8,40}$/i.test(g)) { g = newGuestId(); put('guest', g); } })();
const onlineEndpoint = () => import.meta.env.DEV
  ? `${location.protocol}//${location.hostname}:${Number(location.port) + 1000}`
  : location.origin;
let onlineClient: Client | null = null;
const getOnlineClient = async () => onlineClient ??= new (await import('colyseus.js')).Client(onlineEndpoint());
const resumeKey = 'gd.onlineResume';
interface ResumeInfo { roomId: string; token: string; code: string }
const readSession = <T,>(key: string): T | null => { try { return JSON.parse(sessionStorage.getItem(key) ?? 'null') as T | null; } catch { return null; } };
const writeSession = (key: string, value: unknown) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Private mode may deny storage. */ } };
const clearSession = (key: string) => { try { sessionStorage.removeItem(key); } catch { /* Private mode may deny storage. */ } };
let onlineAuthorized = false;
let onlineSessionKnown = false;
let onlineAuthCheck = 0;

async function checkOnlineAccess(): Promise<boolean> {
  const check = ++onlineAuthCheck;
  const status = $('onlineAuthMsg');
  onlineAuthorized = false;
  $('onlineEntry').classList.add('needs-login');
  btn('btnCreate').disabled = true;
  btn('btnJoin').disabled = true;
  show('onlineAuth');
  status.textContent = '正在检查联机会话…';
  show('passwordAuth', false);
  show('btnWechatLogin', false);
  show('devMockLogin', false);
  try {
    const me = await fetch('/api/me', { credentials: 'include' });
    if (check !== onlineAuthCheck) return false;
    if (me.status === 401) {
      onlineSessionKnown = false;
      const methods = await fetch('/api/auth/methods', { credentials: 'include' });
      if (!methods.ok) throw new Error('联机服务不可用；单机仍可直接游玩');
      const offered = await methods.json() as { password?: boolean; wechatWebsite: boolean; devMock: boolean };
      if (check !== onlineAuthCheck) return false;
      show('passwordAuth', Boolean(offered.password));
      show('btnWechatLogin', offered.wechatWebsite);
      show('devMockLogin', offered.devMock);
      status.textContent = offered.password || offered.wechatWebsite || offered.devMock
        ? '登录后可创建或加入好友房间。' : '联机登录暂不可用；单机仍可直接游玩。';
      if (offered.devMock) status.textContent += ' 本机测试可用开发模拟登录，非微信。';
      return false;
    }
    if (!me.ok) throw new Error('联机服务不可用；单机仍可直接游玩');
    onlineSessionKnown = true;
    const ready = await fetch('/api/online/status', { credentials: 'include' });
    if (check !== onlineAuthCheck) return false;
    if (!ready.ok) throw new Error('联机配置未发布或数据库不可用；单机仍可直接游玩');
    onlineAuthorized = true;
    $('onlineEntry').classList.remove('needs-login');
    ($('onlinePassword') as HTMLInputElement).value = '';
    show('onlineAuth', false);
    btn('btnCreate').disabled = false;
    btn('btnJoin').disabled = false;
    return true;
  } catch (error) {
    if (check === onlineAuthCheck) status.textContent = error instanceof Error ? error.message : '联机服务不可用；单机仍可直接游玩';
    return false;
  }
}

function saveResume(): void {
  if (!online?.room || !online.code) return;
  const token = (online.room as OnlineRoom & { reconnectionToken?: string }).reconnectionToken;
  if (token) writeSession(resumeKey, { roomId: online.room.roomId, token, code: online.code } satisfies ResumeInfo);
}

const netSend = (payload: TableCommand): void => {
  if (!online?.room) return;
  const command: ClientCommand = { ...payload, commandId: newGuestId(), expectedVersion: online.stateVersion } as ClientCommand;
  online.pending.set(command.commandId, command);
  online.room.send('command', command);
};

function bindRoom(state: OnlineState, room: OnlineRoom): void {
  state.room = room;
  state.reconnectAttempt = 0;
  room.onMessage('event', (msg: ServerMsg) => {
    if (online === state && state.room === room) onNet(msg);
  });
  room.onLeave(() => {
    if (online !== state || state.room !== room) return;
    state.room = null;
    $('msg').textContent = '网络断了，正在重连…';
    scheduleReconnect(state);
  });
  room.onError(() => { if (online === state) $('onlineMsg').textContent = '联机服务暂不可用'; });
  room.send('sync', {});
}

async function resolveCode(code: string): Promise<{ roomId: string; inviteToken: string }> {
  const response = await fetch(`/api/rooms/resolve?code=${encodeURIComponent(code)}`, { credentials: 'include' });
  if (!response.ok) throw new Error(response.status === 429 ? '加入太频繁，请稍后再试' : '没有这个房间，核对一下房号');
  return await response.json() as { roomId: string; inviteToken: string };
}

async function connect(mode: 'create' | 'join' | 'resume', code = ''): Promise<void> {
  if (!onlineAuthorized && !await checkOnlineAccess()) return;
  const old = online;
  online = null;
  if (old?.room) void old.room.leave(true);
  if (old?.reconnectTimer !== null && old?.reconnectTimer !== undefined) window.clearTimeout(old.reconnectTimer);
  const state: OnlineState = { room: null, code, seats: [null, null, null, null], phase: 'lobby', inGame: false, host: false, stateVersion: 0, pending: new Map(), reconnectAttempt: 0, reconnectTimer: null };
  online = state;
  $('onlineMsg').textContent = '连接中…';
  try {
    const client = await getOnlineClient();
    const options = { name: myName(), char: settings.me, title: myTitle(), limit: settings.limit };
    const join = async () => {
      const invitation = await resolveCode(code);
      return client.joinById(invitation.roomId, { ...options, inviteToken: invitation.inviteToken });
    };
    let room: OnlineRoom;
    const resume = readSession<ResumeInfo>(resumeKey);
    if (mode === 'resume' && resume?.code === code) {
      try { room = await client.reconnect(resume.token); }
      catch { room = await join(); }
    } else if (mode === 'create') room = await client.create('guandan', options);
    else room = await join();
    if (online !== state) { void room.leave(true); return; }
    bindRoom(state, room);
    $('onlineMsg').textContent = '';
  } catch (error) {
    if (online !== state) return;
    const message = error instanceof Error ? error.message : '连不上联机服务，稍后再试';
    $('onlineMsg').textContent = message;
    if (/没有这个房间|进不去/.test(message)) { put('room', null); clearSession(resumeKey); }
    if (/登录|401|UNAUTHORIZED/.test(message)) void checkOnlineAccess();
  }
}

function scheduleReconnect(state: OnlineState): void {
  if (state.reconnectTimer !== null) return;
  const delay = Math.min(10_000, 1500 * 2 ** state.reconnectAttempt++);
  state.reconnectTimer = window.setTimeout(() => {
    state.reconnectTimer = null;
    if (online === state && !state.room) void reconnectOnline(state);
  }, delay);
}

async function reconnectOnline(state: OnlineState): Promise<void> {
  try {
    const resume = readSession<ResumeInfo>(resumeKey);
    if (!resume || resume.code !== state.code) throw new Error('连接已过期');
    const room = await (await getOnlineClient()).reconnect(resume.token);
    if (online !== state) { void room.leave(true); return; }
    bindRoom(state, room);
    for (const command of state.pending.values()) room.send('command', command);
  } catch {
    if (online === state) {
      $('msg').textContent = '网络断了，正在重连…';
      if (readSession<ResumeInfo>(resumeKey)) scheduleReconnect(state);
      else $('msg').textContent = '重连凭据已失效，请从首页重新进入房间';
    }
  }
}

function onNet(msg: ServerMsg): void {
  if (!online) return;
  if (msg.t === 'ack') {
    online.pending.delete(msg.commandId);
    if (!msg.ok) {
      sfx('error');
      $(online.inGame ? 'msg' : 'lobbyMsg').textContent = msg.msg ?? '操作未生效';
      if (online.inGame) render();
    }
    return;
  }
  if (msg.stateVersion !== undefined && msg.stateVersion < online.stateVersion) return;
  if (msg.stateVersion !== undefined) online.stateVersion = Math.max(online.stateVersion, msg.stateVersion);
  switch (msg.t) {
    case 'error':
      sfx('error');
      if (!online.code && /没有这个房间|进不去/.test(msg.msg)) put('room', null);
      $(online.inGame ? 'msg' : online.code ? 'lobbyMsg' : 'onlineMsg').textContent = msg.msg;
      if (online.inGame) render(); // 被拒后恢复按钮
      return;
    case 'room': {
      online.code = msg.code;
      put('room', msg.phase === 'final' ? null : { code: msg.code, at: Date.now() }); // 刷新页面后能回到房间
      saveResume();
      online.seats = msg.seats;
      online.phase = msg.phase;
      online.host = !!msg.seats[0]?.host;
      if (msg.phase === 'lobby') { renderLobby(); return; }
      setCast({ 1: msg.seats[1]!.char, 2: msg.seats[2]!.char, 3: msg.seats[3]!.char }); refreshNames();
      if (msg.phase === 'settling') {
        $('msg').textContent = '正在结算，入账前不会显示赛果';
        show('btnRetrySettle', online.host);
      } else show('btnRetrySettle', false);
      if (msg.phase === 'final' && online.inGame && m.winner !== null) showFinal();
      if (online.inGame) render();
      return;
    }
    case 'state': {
      const fresh = !online.inGame || msg.m.roundNo !== m.roundNo || msg.m.results.length !== m.results.length;
      m = msg.m;
      if (!online.inGame) enterOnlineGame();
      if (fresh) { show('ovResult', false); show('ovReturn', false); resetTable(); } else { lastShown.clear(); handKey = ''; step(); }
      return;
    }
    case 'act': {
      const before = snapshot();
      m = msg.m;
      if (msg.seat === ME) { selected.clear(); $('msg').textContent = ''; btn('btnPlay').classList.remove('invalid'); clearChoices(); }
      after(msg.seat, msg.play, before);
      step();
      return;
    }
    case 'askReturn': {
      $('returnInfo').textContent = `${NAMES[msg.from]}进贡给你 ${cardName(msg.card)}，选一张还给${NAMES[msg.from]}：`;
      const level = m.teamLevel[m.results.at(-1)!.winnerTeam];
      $('returnPick').replaceChildren(...sortHand(msg.options, level).reverse().map((c) => {
        const el = cardEl(c, level, 'button');
        el.onclick = () => { show('ovReturn', false); netSend({ t: 'return', id: c.id }); };
        return el;
      }));
      show('ovResult', false);
      show('ovReturn');
      return;
    }
    case 'waiting': {
      const names = msg.seats.filter((x) => x !== ME).map((x) => NAMES[x]);
      if (btn('btnNext').disabled) btn('btnNext').textContent = names.length ? `等${names.join('、')}…` : '马上开始…';
      return;
    }
  }
}

function enterOnlineGame(): void {
  if (!online) return;
  online.inGame = true;
  clearTimeout(timer);
  stats = { start: Date.now(), bombs: 0, firsts: 0, level: 'normal' };
  gate.reset();
  void voice.preload([NARRATOR.id, online.seats[0]!.char, ...online.seats.slice(1).map((x) => x!.char)]);
  for (const id of ['ovOnline', 'ovHome', 'ovResult', 'ovReturn', 'ovFinal', 'ovMenu', 'ovSetup']) show(id, false);
  sound.music.switchTo('table');
}

function leaveOnline(): void {
  put('room', null);
  clearSession(resumeKey);
  if (!online) return;
  const o = online;
  online = null;
  show('btnRetrySettle', false);
  if (o.reconnectTimer !== null) window.clearTimeout(o.reconnectTimer);
  if (o.room) void o.room.leave(true);
  setCast(settings.cast); refreshNames();
}

/** 联机入口：选角色、输入房号加入或创建房间。 */
function openOnline(): void {
  show('ovHome', false);
  show('onlineEntry'); show('onlineLobby', false);
  $('onlineMsg').textContent = '';
  $('onlineMe').replaceChildren(...ROSTER.map((c) => {
    const b = Object.assign(document.createElement('button'), { className: `choice char${c.id === settings.me ? ' on' : ''}` });
    b.innerHTML = `${avatarHtml(c.id, c.avatar)}<b>${c.name}</b>`;
    b.onclick = () => { settings.me = c.id; put('me', c.id); openOnline(); sfx('button'); };
    return b;
  }));
  show('ovOnline');
}
/** 两小时内进过还没打完的房间：首页按钮变成“回到房间”。 */
const lastRoom = () => { const r = store<{ code: string; at: number } | null>('room', null); return r && Date.now() - r.at < 2 * 3600_000 ? r.code : null; };
btn('btnOnline').onclick = () => {
  sfx('button');
  if (online?.room) {
    if (online.inGame) { show('ovHome', false); render(); }
    else { openOnline(); renderLobby(); }
    return;
  }
  const code = lastRoom();
  openOnline();
  void checkOnlineAccess().then((ready) => { if (ready && code) void connect('resume', code); });
};
btn('btnWechatLogin').onclick = () => { location.assign('/api/auth/wechat/start'); };
const passwordErrorMessage = (status: number, code: string): string => {
  if (status === 409 && code === 'USERNAME_TAKEN') return '用户名已被使用，请换一个。';
  if (status === 401 && code === 'INVALID_CREDENTIALS') return '用户名或密码不正确。';
  if (status === 400) return '用户名或密码格式不符合要求，请检查后重试。';
  if (status === 429) return '操作太频繁，请稍后再试。';
  if (status === 403 && code === 'ORIGIN_DENIED') return '页面来源校验失败，请从正式入口打开。';
  return '联机登录暂不可用，请稍后重试；单机仍可直接游玩。';
};
let passwordSubmitting = false;
($('passwordAuth') as HTMLFormElement).addEventListener('submit', (event) => {
  event.preventDefault();
  if (passwordSubmitting) return;
  const action = (event as SubmitEvent).submitter?.id === 'btnPasswordRegister' ? 'register' : 'login';
  const usernameInput = $('onlineUsername') as HTMLInputElement;
  const passwordInput = $('onlinePassword') as HTMLInputElement;
  const username = usernameInput.value.trim();
  const password = passwordInput.value;
  const status = $('onlineAuthMsg');
  if (!username || username.length > 32) { status.textContent = '请输入不超过 32 位的用户名。'; return; }
  if (Array.from(password).length < 12 || Array.from(password).length > 128 || /[\u0000\r\n\u0085\u2028\u2029]/u.test(password)) { status.textContent = '密码须为 12–128 位，不能包含换行。'; return; }
  passwordInput.autocomplete = action === 'register' ? 'new-password' : 'current-password';
  passwordSubmitting = true;
  btn('btnPasswordRegister').disabled = true;
  btn('btnPasswordLogin').disabled = true;
  status.textContent = action === 'register' ? '正在注册…' : '正在登录…';
  void (async () => {
    try {
      const response = await fetch(`/api/auth/password/${action}`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (response.status !== (action === 'register' ? 201 : 200)) {
        const body = await response.json().catch(() => null) as { code?: unknown } | null;
        status.textContent = passwordErrorMessage(response.status, typeof body?.code === 'string' ? body.code : '');
        return;
      }
      passwordInput.value = '';
      await checkOnlineAccess();
    } catch {
      status.textContent = '网络暂不可用，请稍后重试；单机仍可直接游玩。';
    } finally {
      passwordSubmitting = false;
      btn('btnPasswordRegister').disabled = false;
      btn('btnPasswordLogin').disabled = false;
    }
  })();
});
btn('btnDevMockLogin').onclick = () => { void (async () => {
  const account = ($('devMockAccount') as HTMLInputElement).value.trim();
  if (!/^[a-z0-9_-]{1,32}$/i.test(account)) { $('onlineAuthMsg').textContent = '测试账号须为 1–32 位字母、数字、下划线或连字符'; return; }
  const response = await fetch('/api/__dev/mock-login', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ account }) });
  if (!response.ok) { $('onlineAuthMsg').textContent = '开发模拟登录失败；单机仍可直接游玩'; return; }
  await checkOnlineAccess();
})().catch(() => { $('onlineAuthMsg').textContent = '开发模拟登录失败；单机仍可直接游玩'; }); };
btn('btnOnlineBack').onclick = () => { ($('onlinePassword') as HTMLInputElement).value = ''; show('ovOnline', false); openHome(); };
btn('btnCreate').onclick = () => { sfx('button'); void connect('create'); };
btn('btnJoin').onclick = () => {
  const code = ($('roomCode') as HTMLInputElement).value.trim();
  if (!isRoomCode(code)) { sfx('error'); $('onlineMsg').textContent = '房号是 4 位数字'; return; }
  sfx('button');
  void connect('join', code);
};
($('roomCode') as HTMLInputElement).addEventListener('keydown', (e) => { if (e.key === 'Enter') btn('btnJoin').click(); });
btn('btnLeave').onclick = () => { leaveOnline(); openOnline(); };
btn('btnOnlineReady').onclick = () => { sfx('button'); netSend({ t: 'ready', ready: !online?.seats[0]?.ready }); };
btn('btnOnlineStart').onclick = () => { sfx('button'); netSend({ t: 'start' }); };
btn('btnRetrySettle').onclick = () => { sfx('button'); netSend({ t: 'retrySettle' }); };

/** 房间等人：四个座位按牌桌摆（对家在上、上家在左、下家在右、我在下），空位写“等朋友 / 电脑补位”。 */
function renderLobby(): void {
  if (!online) return;
  show('onlineEntry', false); show('onlineLobby');
  $('roomCodeBig').textContent = online.code;
  const cell = (i: number) => {
    const x = online!.seats[i];
    const d = Object.assign(document.createElement('div'), { className: `lseat p${i}${i % 2 === 0 ? ' us' : ' them'}${x ? '' : ' empty'}` });
    const c = x ? characterById(x.char)! : null;
    d.innerHTML = c
      ? `${avatarHtml(c.id, c.avatar)}<b>${i === 0 ? '我' : c.name}</b><span>${POS[i]}${x!.host ? ' · 房主' : ''}${x!.online ? x!.ready ? ' · 已准备' : ' · 未准备' : ' · 掉线'}</span>`
      : `<i class="face-av ghost"><span>？</span></i><b>等朋友加入</b><span>${POS[i]} · 开打时电脑补上</span>`;
    return d;
  };
  $('lobbySeats').replaceChildren(cell(2), cell(3), cell(1), cell(0));
  const humans = online.seats.filter((x) => x?.human).length;
  btn('btnOnlineReady').textContent = online.seats[0]?.ready ? '取消准备' : '准备';
  btn('btnOnlineStart').hidden = !online.host;
  btn('btnOnlineStart').textContent = humans < 4 ? `开打（电脑补 ${4 - humans} 位）` : '开打';
  btn('btnOnlineStart').disabled = !online.seats.filter((seat) => seat?.human).every((seat) => seat?.online && seat.ready);
  $('lobbyMsg').textContent = online.host ? '' : '准备后等房主开打…';
}

// ── 开局：首页 → 开局设置 → 发牌 ──
function newMatch(): void {
  clearTimeout(timer);
  nextSeed = (nextSeed * 7 + 13) % 1e9;
  m = createMatch(nextSeed, rulesFor(settings.limit));
  stats = { start: Date.now(), bombs: 0, firsts: 0, level: settings.level };
  setCast(settings.cast); refreshNames();
  gate.reset();
  void voice.preload([NARRATOR.id, settings.me, ...Object.values(settings.cast)]);
  for (const id of ['ovResult', 'ovReturn', 'ovRules', 'ovFinal', 'ovMenu', 'ovSetup', 'ovHome', 'ovCounter']) show(id, false);
  sound.music.switchTo('table');
  resetTable();
}
function resetTable(): void {
  lastShown.clear();
  selected.clear();
  dealing = settings.fx;
  handKey = '';
  $('msg').textContent = '';
  clearChoices();
  roundNote();
  step();
  save();
  // 新一轮第一副：只放开场旁白，角色不说话（作者 11-02）
  if (!m.results.length) { narrate(); return; }
  // 第 2 副起：播报员报进贡、还牌或抗贡
  if (m.round.resisted && m.results.length) {
    later(600, () => call('resist'));
    // 抗贡的人：上一副输的那队里手上有大王的（两张大王抗贡）
    const lose = (1 - m.results.at(-1)!.winnerTeam) as 0 | 1;
    later(600, () => { for (const s of [0, 1, 2, 3] as Seat[]) if (s % 2 === lose && m.round.hands[s].some((c) => c.rank === 17)) resistFx(s); });
  } else if (m.round.tributes.length) {
    later(600, () => { call('tribute'); for (const t of m.round.tributes) if (settings.fx) flyFx('fx-tribute', '贡', t.from as Seat, t.to as Seat); });
    later(1500, () => { call('returnCard'); for (const t of m.round.tributes) if (settings.fx) flyFx('fx-return', '还', t.to as Seat, t.from as Seat); });
  }
}

function openSetup(): void {
  const mark = (rowId: string, attr: string, val: string) => $(rowId).querySelectorAll<HTMLElement>('.choice').forEach((b) => b.classList.toggle('on', b.dataset[attr] === val));
  mark('levelPick', 'level', settings.level);
  mark('limitPick', 'limit', String(settings.limit));
  // 选我的角色（8 选 1）
  $('mePick').replaceChildren(...ROSTER.map((c) => {
    const b = document.createElement('button');
    b.className = `choice char${c.id === settings.me ? ' on' : ''}`;
    b.dataset.char = c.id;
    b.innerHTML = `${avatarHtml(c.id, c.avatar)}<b>${c.name}</b><span>${c.tagline}</span>`;
    return b;
  }));
  // 今天的牌友：对家（搭档）、下家、上家
  $('friends').replaceChildren(...[2, 1, 3].map((s) => {
    const c = characterById(settings.cast[s as 1 | 2 | 3])!;
    const d = document.createElement('div');
    if (s === 2) d.className = 'partner';
    d.innerHTML = `${avatarHtml(c.id, c.avatar)}<b>${c.name} · ${POS[s]}</b><span>${c.tagline}</span>`;
    return d;
  }));
  show('ovHome', false);
  show('ovSetup');
}
/** 开局分两步：选角色 → 牌局设置（作者：“界面设计不要太拥挤”）。 */
function setupStep(n: 1 | 2): void {
  const saved = savedMatch();
  $('setupWarn').hidden = !saved;
  if (saved) $('setupWarn').textContent = `开打后，没打完的那一局（第 ${saved.m.roundNo} 副，我方到${stationOf(saved.m.teamLevel[0])}）会被放弃。想接着打，回首页点“生涯战绩”。`;
  show('setupStep1', n === 1);
  show('setupStep2', n === 2);
  $('ovSetup').querySelector('.panel')!.scrollTo(0, 0);
}
btn('btnSetupNext').onclick = () => { setupStep(2); sfx('button'); };
btn('btnSetupPrev').onclick = () => { setupStep(1); sfx('button'); };
$('levelPick').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.choice');
  if (!b) return;
  settings.level = b.dataset.level as Difficulty; put('level', settings.level); applySettings(); openSetup(); sfx('button');
});
$('limitPick').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.choice');
  if (!b) return;
  settings.limit = Number(b.dataset.limit); put('limit', settings.limit); applySettings(); openSetup(); sfx('button');
});
/** 头像：有图用图（public/avatars/<id>.webp），没图显示一个字。 */
/** 头像：透明底人物图 + 角色淡色圆底；图加载成功就去掉占位字，加载失败留字。 */
function avatarInner(id: string, ch: string): string {
  return `<span>${ch}</span><img src="avatars/${id}.webp" alt="" onload="this.previousElementSibling?.remove()" onerror="this.remove()">`;
}
function avatarHtml(id: string, ch: string): string {
  return `<i class="face-av" style="--tint:${characterById(id)?.tint ?? '#e4ded2'}">${avatarInner(id, ch)}</i>`;
}
/** 牌友从其余 7 人里随机挑 3 个；我的角色不能出现在牌友里。 */
function rerollCast(): void {
  const pool = ROSTER.map((c) => c.id).filter((id) => id !== settings.me).sort(() => Math.random() - 0.5);
  settings.cast = { 2: pool[0], 1: pool[1], 3: pool[2] };
  put('cast', settings.cast);
}
$('mePick').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('.choice');
  if (!b) return;
  settings.me = b.dataset.char!; put('me', settings.me);
  if (Object.values(settings.cast).includes(settings.me)) rerollCast();
  void voice.preload([settings.me]); void voice.sayFile(settings.me, 'idle_1.mp3'); // 选中时听一句口头禅
  openSetup(); sfx('button');
});
btn('btnReroll').onclick = () => { rerollCast(); openSetup(); sfx('button'); };
btn('btnSetupGo').onclick = () => { sfx('button'); newMatch(); };
btn('btnSetupBack').onclick = () => { show('ovSetup', false); openHome(); };

/** 首页右上角“我的档案”卡片：头像、昵称、江湖名号。 */
function renderProfileChip(): void {
  const ch = characterById(settings.me);
  const av = $('pcAv');
  av.style.setProperty('--tint', ch?.tint ?? '#e4ded2');
  av.innerHTML = avatarInner(settings.me, ch?.avatar ?? '我');
  $('pcName').textContent = myName();
  $('pcTitle').textContent = TITLES[titleIndex(career())].name;
}
let careerFrom: 'home' | 'menu' = 'home'; // 从牌桌菜单进档案时，关掉回到牌桌
const canOpenProfile = () => !$('ovUserCenter').hidden ||
  (!$(careerFrom === 'home' ? 'ovHome' : 'ovMenu').hidden &&
    [...document.querySelectorAll<HTMLElement>('.overlay')].every((overlay) => overlay.hidden || overlay.id === 'ovMenu'));
let userCenter: Promise<{ open(closeLabel: string): Promise<void> }> | null = null;
const getUserCenter = () => userCenter ??= import('./user-center.ts').then(({ createUserCenter }) => createUserCenter({
  onGuest: () => { onlineSessionKnown = false; show('ovMenu', false); showCareer(); },
  onOpen: () => { show('ovMenu', false); show('ovFinal', false); },
  onClose: () => {
    const from = careerFrom;
    if (from === 'home') openHome();
    careerFrom = 'home';
    btn(from === 'home' ? 'btnProfile' : 'btnMenu').focus();
  },
  canOpen: canOpenProfile,
  hasSession: () => onlineSessionKnown,
}));
const openMyProfile = (from: 'home' | 'menu') => {
  careerFrom = from;
  sfx('button');
  void getUserCenter().then((center) => center.open(from === 'home' ? '返回首页' : '回到牌桌')).catch(() => {
    if (canOpenProfile()) { show('ovMenu', false); showCareer(); }
  });
};
btn('btnProfile').onclick = () => openMyProfile('home');
btn('btnMenuProfile').onclick = () => openMyProfile('menu');
function openHome(): void {
  renderProfileChip();
  clearTimeout(timer);
  // 首页逻辑（作者 11-02，表见 docs/首页与进度.md）：没有“继续上一局”；打过的人从“生涯战绩”看到在哪一站、开始下一局
  const saved = savedMatch();
  const c = career();
  const hasRecord = !!saved || c.matches > 0;
  btn('btnCareer').hidden = !hasRecord;
  const jh = titleIndex(c) > 0 ? `${TITLES[titleIndex(c)].name} · ` : ''; // 首页也看得到自己的名号（洗牌童子不显示）
  $('careerHint').textContent = jh + (saved ? `我方到${stationOf(saved.m.teamLevel[0])}` : c.matches ? `${c.matches} 场${compact.matches ? '' : ` · 胜率 ${Math.round((c.wins / c.matches) * 100)}%`}` : ''); // 手机菜单窄，只写名号与场数
  const room = lastRoom();
  btn('btnOnline').innerHTML = room ? `回到房间<small>房号 ${room}</small>` : '好友联机<small>2 对 2 · 发房号</small>';
  // 第一次来（没打过、没看过教学）：新手教学挂“推荐”
  btn('btnTeach').classList.toggle('reco', !hasRecord && tipsDone.size === 0);
  for (const id of ['ovMenu', 'ovRules', 'ovCounter', 'ovResult', 'ovFinal', 'ovSetup', 'ovReturn', 'ovUserCenter']) show(id, false);
  show('coach', false); show('coachSpot', false);
  show('ovHome');
}
/** 没打完的那一局（本机存档）。 */
const savedMatch = () => store<{ m: Match; stats: MatchStats } | null>('save', null);
/** 从存档接着打：在这一副开头就“开始第 N 副”，打到一半就“接着打第 N 副”。 */
function resumeSaved(): void {
  const saved = savedMatch();
  if (!saved) return;
  m = saved.m;
  stats = saved.stats;
  setCast(settings.cast); refreshNames();
  void voice.preload([NARRATOR.id, settings.me, ...Object.values(settings.cast)]);
  show('ovHome', false);
  lastShown.clear();
  handKey = '';
  roundNote();
  $('msg').textContent = m.round.history.length ? `接着打第 ${m.roundNo} 副。` : '';
  for (const id of ['ovFinal', 'ovHome']) show(id, false);
  step();
}
btn('btnStart').onclick = () => { sfx('button'); setupStep(1); openSetup(); };
btn('btnTeach').onclick = () => {
  settings.tut = true; put('tut', true);
  tipsDone = new Set(); put('tips', []); tipQueue.length = 0;
  settings.level = 'easy'; put('level', 'easy');
  applySettings();
  newMatch();
};
btn('btnCareer').onclick = showCareer;

/** 本副说明（翻牌定先 / 进贡还牌 / 抗贡）：电脑版放操作栏右侧；手机版放提示行（开局显示，出牌后被提示覆盖）。 */
function roundNote(): void {
  const r = m.round;
  const f = r.flipped;
  // 作者 11-03：每条单独一行、不用逗号（原来一行用逗号分号连着，长了被截断）
  const lines = f && m.roundNo === 1 ? [`翻牌定先 翻出 ${cardName(f)}`, `${NAMES[r.lead]}先出`]
    : r.resisted ? ['抗贡', '上游先出'] : [...r.tributes.flatMap((x) => [`${NAMES[x.from]}进贡 ${cardName(x.card)}`, `${NAMES[x.to]}还 ${cardName(x.back!)}`]), `${NAMES[r.lead]}先出`];
  $('note').replaceChildren(...lines.map((l) => Object.assign(document.createElement('span'), { textContent: l })));
  $('note').title = lines.join('\n');
  if (compact.matches) $('msg').textContent = lines.join('  '); // 手机按钮下只有一行：用空格隔开，不用逗号
}

// ── 记牌器 ──
function openCounter(): void {
  // 记牌器（作者反馈“看不懂”后重做）：每个点数一格，写明“外面还有 N 张”，下面一排圆点代表这个点数的全部牌：
  // 红 = 还在别人手里，绿 = 在你手里，灰 = 已经打出。顶部一句话总结大小王和可能的炸弹。
  const v = viewOf(m, ME);
  const left = unseenCounts(v.hand, v.played);
  const mine = (r: number) => v.hand.filter((c) => c.rank === r).length;
  const gone = (r: number) => v.played.filter((c) => c.rank === r).length;
  const bombs = COUNTER_RANKS.filter((r) => r < 16 && (left.get(r) ?? 0) >= 4).map(rankName);
  $('counterSummary').innerHTML = `外面还有：<b>大王 ${left.get(17)} 张、小王 ${left.get(16)} 张</b>；`
    + (bombs.length ? `可能还有炸弹（外面还剩 4 张以上）的点数：<b>${bombs.join('、')}</b>` : '外面已经凑不出四张的炸弹');
  $('counterGrid').replaceChildren(...COUNTER_RANKS.map((r) => {
    const total = r >= 16 ? 2 : 8, out = left.get(r) ?? 0, my = mine(r), pl = gone(r);
    const d = document.createElement('div');
    d.className = `${out === 0 ? 'zero' : ''}${r === m.round.level ? ' lvl' : ''}`;
    const dots = [...Array(out).fill('o'), ...Array(my).fill('m'), ...Array(Math.max(0, total - out - my)).fill('p')].map((k) => `<i class="${k}"></i>`).join('');
    d.innerHTML = `<em>${rankName(r)}${r === m.round.level ? '（级牌）' : ''}</em><b>${out}</b><span>外面还有</span><p class="dots">${dots}</p>`;
    d.title = `${rankName(r)}：共 ${total} 张，外面 ${out}，你手里 ${my}，已出 ${pl}`;
    return d;
  }));
  show('ovMenu', false);
  show('ovCounter');
}
btn('btnCounter').onclick = openCounter;
btn('btnMenuCounter').onclick = openCounter;
btn('btnCloseCounter').onclick = () => show('ovCounter', false);

// ── 游戏中菜单（暂停）与规则设置 ──
function applySettings(): void {
  document.body.classList.toggle('fx', settings.fx);
  document.body.dataset.skin = settings.skin;
  for (const b of document.querySelectorAll<HTMLElement>('.skin-opt')) b.setAttribute('aria-checked', String(b.dataset.skin === settings.skin));
  ($('optFx') as HTMLInputElement).checked = settings.fx;
  ($('optTalk') as HTMLInputElement).checked = settings.talk;
  ($('optSound') as HTMLInputElement).checked = settings.sound;
  ($('optTut') as HTMLInputElement).checked = settings.tut;
  ($('optVoice') as HTMLInputElement).checked = settings.voice;
  ($('optLevel') as HTMLSelectElement).value = settings.level;
  ($('optLimit') as HTMLSelectElement).value = String(settings.limit);
}
const bindCheck = (id: string, key: 'fx' | 'talk' | 'sound' | 'tut' | 'voice') => {
  ($(id) as HTMLInputElement).onchange = (e) => {
    settings[key] = (e.target as HTMLInputElement).checked;
    put(key, settings[key]);
    if (key === 'sound') sound.setMuted(!settings.sound);
    applyVoice();
    if (key === 'tut' && settings.tut) { tipsDone = new Set(); put('tips', []); }
    applySettings();
  };
};
for (const b of document.querySelectorAll<HTMLElement>('.skin-opt')) b.onclick = () => { settings.skin = b.dataset.skin as Skin; put('skin', settings.skin); applySettings(); sfx('button'); handKey = ''; render(); };
bindCheck('optFx', 'fx'); bindCheck('optTalk', 'talk'); bindCheck('optSound', 'sound'); bindCheck('optTut', 'tut'); bindCheck('optVoice', 'voice');
($('optLevel') as HTMLSelectElement).onchange = (e) => { settings.level = (e.target as HTMLSelectElement).value as Difficulty; put('level', settings.level); };
($('optLimit') as HTMLSelectElement).onchange = (e) => { settings.limit = Number((e.target as HTMLSelectElement).value); put('limit', settings.limit); };
// 图解规则（作者要求图文版）：每条规则配真牌面示例；座位与升级用小示意图
let guideBuilt = false;
function buildGuide(): void {
  if (guideBuilt) return;
  guideBuilt = true;
  const box = $('rulesGuide');
  for (const sec of GUIDE) {
    const h = Object.assign(document.createElement('h3'), { textContent: sec.title });
    box.append(h);
    for (const it of sec.items) {
      const row = document.createElement('div');
      row.className = 'gitem';
      const txt = document.createElement('div');
      txt.className = 'gtext';
      txt.innerHTML = `<b>${it.title}</b><p>${it.text}</p>`;
      const pic = document.createElement('div');
      pic.className = 'gpic';
      if (it.arrows?.[0] === 'seats') {
        pic.innerHTML = '<div class="seats"><i class="us">对家<br>老顾</i><i class="them l">上家<br>苏小算</i><i class="them r">下家<br>雷哥</i><i class="us me">我</i><span class="ccw">↺ 逆时针</span></div>';
      } else if (it.arrows?.[0] === 'upgrade') {
        pic.innerHTML = '<div class="upg"><div><em>上游</em><em>二游</em><b>+3</b></div><div><em>上游</em><em class="g">三游</em><b>+2</b></div><div><em>上游</em><em class="g">下游</em><b>+1</b></div><span>第二格是上游的搭档</span></div>';
      } else {
        (it.cards ?? []).forEach((cards, i) => {
          const g = document.createElement('div');
          g.className = 'mini gcards';
          g.append(...cards.map((x) => cardEl(x, it.level ?? 0)));
          pic.append(g);
          const arrow = it.arrows?.[i];
          if (arrow) pic.append(Object.assign(document.createElement('span'), { className: 'garrow', textContent: arrow }));
        });
      }
      row.append(pic, txt);
      box.append(row);
    }
  }
}
function rulesTab(which: 'rules' | 'settings'): void {
  btn('tabRules').classList.toggle('on', which === 'rules');
  btn('tabSettings').classList.toggle('on', which === 'settings');
  show('rulesGuide', which === 'rules');
  show('settingsBody', which === 'settings');
}
btn('tabRules').onclick = () => { rulesTab('rules'); sfx('button'); };
btn('tabSettings').onclick = () => { rulesTab('settings'); sfx('button'); };
const openRules = () => { buildGuide(); show('ovMenu', false); show('ovRules'); };
btn('btnRules').onclick = openRules;
btn('btnMenuRules').onclick = openRules;
btn('btnHomeRules').onclick = openRules;
btn('btnCloseRules').onclick = () => show('ovRules', false);
btn('btnHome2').onclick = () => { save(); leaveOnline(); openHome(); };
btn('btnMenu').onclick = () => { sfx('button'); show('ovMenu'); };
btn('btnResume').onclick = () => show('ovMenu', false);
btn('btnMenuHome').onclick = () => { save(); leaveOnline(); openHome(); };
portrait.addEventListener('change', () => { handKey = ''; render(); });

// ── 调试入口（?debug=1，界面测试用）：拿到对局，直接摆出一副结束或比赛结束的局面 ──
if (params.get('debug') === '1') {
  (window as unknown as { __gd: unknown }).__gd = {
    get m() { return m; },
    endRound(order: Seat[]) {
      clearTimeout(timer);
      const r = m.round;
      r.finished = order.slice(0, 3);
      r.over = true;
      m.results.push({ order, winnerTeam: (order[0] % 2) as 0 | 1, steps: 1, passedA: false });
      showResult();
    },
    endMatch(winner: 0 | 1) {
      clearTimeout(timer);
      m.round.over = true;
      m.winner = winner;
      m.results.push({ order: winner === 0 ? [0, 2, 1, 3] : [1, 3, 0, 2], winnerTeam: winner, steps: 3, passedA: true });
      showFinal();
    },
  };
}

/**
 * 弹层分三段：标题（h2 / 分页标签）固定在上、内容区单独滚动、按钮行固定在下——内容滚动时不会钻到按钮下面被遮挡
 * （作者：“按钮和卡牌不能有任何遮挡”“间距呢？”）。开局设置的两步各自分段。
 */
function layoutPanels(): void {
  const targets = [...document.querySelectorAll<HTMLElement>('.overlay .panel')].flatMap((p) => (p.querySelector(':scope > section') ? [...p.querySelectorAll<HTMLElement>(':scope > section')] : [p]));
  for (const box of targets) {
    if (box.querySelector(':scope > .pbody')) continue;
    const kids = [...box.children] as HTMLElement[];
    const foot = kids.length && kids[kids.length - 1].matches('.row.center') ? kids[kids.length - 1] : null;
    const head = kids.filter((k, i) => k !== foot && kids.slice(0, i + 1).every((x) => x.matches('h2, .tabs, .step')));
    const body = kids.filter((k) => k !== foot && !head.includes(k));
    if (!body.length) continue;
    const wrap = Object.assign(document.createElement('div'), { className: 'pbody' });
    box.insertBefore(wrap, body[0]);
    wrap.append(...body);
  }
}
layoutPanels();

applySettings();
if (testMode) { void voice.preload([NARRATOR.id, settings.me, ...Object.values(settings.cast)]); resetTable(); }
else { openHome(); idle(() => { if (!document.body.classList.contains('at-home')) return; render(); }); } // 性能（11-03）：首页盖住牌桌，牌桌等空闲时再在后台画好，不挡首页出来（启动时省一次约 300ms 的长任务）
syncAtHome(); // 测试模式直接进牌桌：去掉 at-home、换上人头牌图；正常启动停在首页，保持 at-home
